import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Square, Hexagon, Loader2, Maximize2, Minimize2, Trash2, PenLine, DoorOpen, Ruler, Lock, Move, Undo2, Redo2, Combine, Ungroup } from 'lucide-react';
import { buildHousePreset, geomFromCanvas } from './housePresets';
import type { FPRoom, FPPoint, FPDoor } from './FloorPlanEditor';

// House element shapes. 'stairs' is a special rectangle (drawn with treads).
const H_TYPES: { key: string; label: string; color: string }[] = [
  { key: 'rect', label: 'Rectangle', color: '#ffffff' },
  { key: 'circle', label: 'Circle', color: '#ffffff' },
  { key: 'trapezium', label: 'Trapezium', color: '#ffffff' },
  { key: 'stairs', label: 'Stairs', color: '#ffffff' },
];
const H_COLOR = (t: string) => (H_TYPES.find((r) => r.key === t)?.color) || '#ffffff';
let _hid = 0;
const huid = () => 'h' + (++_hid) + '_' + Date.now().toString(36);
const rectPts = (x0: number, y0: number, x1: number, y1: number): FPPoint[] => [
  { x: Math.min(x0, x1), y: Math.min(y0, y1) }, { x: Math.max(x0, x1), y: Math.min(y0, y1) },
  { x: Math.max(x0, x1), y: Math.max(y0, y1) }, { x: Math.min(x0, x1), y: Math.max(y0, y1) },
];
// Build a shape's polygon points inside the box (x0,y0)-(x1,y1). Circle is a
// 48-gon (renders smooth + insets cleanly for double walls); trapezium is a
// narrower-top quad; rect/stairs/anything else is a rectangle.
const makeShapePoints = (shape: string, x0: number, y0: number, x1: number, y1: number): FPPoint[] => {
  const ax0 = Math.min(x0, x1), ax1 = Math.max(x0, x1), ay0 = Math.min(y0, y1), ay1 = Math.max(y0, y1);
  const w = ax1 - ax0, h = ay1 - ay0;
  if (shape === 'circle') {
    const cx = (ax0 + ax1) / 2, cy = (ay0 + ay1) / 2, rx = w / 2, ry = h / 2;
    return Array.from({ length: 48 }, (_, i) => { const a = (i / 48) * 2 * Math.PI; return { x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) }; });
  }
  if (shape === 'trapezium') return makeTrapeziumPoints(ax0, ay0, ax1, ay1, 'top', 0.22);
  return rectPts(ax0, ay0, ax1, ay1);
};
// Trapezium with the NARROW edge on a given side, tapered by `inset` (0..0.45).
const makeTrapeziumPoints = (x0: number, y0: number, x1: number, y1: number, dir: string, inset: number): FPPoint[] => {
  const w = x1 - x0, h = y1 - y0, iw = w * inset, ih = h * inset;
  if (dir === 'bottom') return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1 - iw, y: y1 }, { x: x0 + iw, y: y1 }];
  if (dir === 'left') return [{ x: x0, y: y0 + ih }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 - ih }];
  if (dir === 'right') return [{ x: x0, y: y0 }, { x: x1, y: y0 + ih }, { x: x1, y: y1 - ih }, { x: x0, y: y1 }];
  return [{ x: x0 + iw, y: y0 }, { x: x1 - iw, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }]; // top (default)
};
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Boundary editor that works on the ACTUAL rendered figure (a live clone of the
 * iframe's #mapSvg), with draggable corner handles. The iframe is same-origin so
 * we read its SVG + __plotGeom directly. Corner drags reshape the boundary and
 * (on release) push new dimensions into the engine via SET_BOUNDARIES, then the
 * figure is re-snapshotted so it always shows the real drawing.
 */
interface Pt { x: number; y: number }
// vbX/vbY/vbW/vbH = the cropped viewBox (figure bbox) in SVG user coords.
interface Snap { canvasPts: Pt[]; scale: number; vbX: number; vbY: number; vbW: number; vbH: number; svg: SVGElement; roads: any; figCx: number; figCy: number; figW: number; figH: number }
type Shape = 'rectangle' | 'trapezoid';
interface Props {
  open: boolean;
  getIframe: () => HTMLIFrameElement | null;
  onClose: () => void;
  initialPreset?: string;        // apply this preset once when opened (from the template picker)
  onPresetApplied?: () => void;
}

const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
const r1 = (x: number) => Math.round(x * 10) / 10;
// canvasPts order is [SW, SE, NE, NW]; the corner kept fixed is the dragged
// corner's opposite — map each index to a SET_BOUNDARIES anchor name.
const ANCHOR_FOR = ['bottom-left', 'bottom-right', 'top-right', 'top-left'];
const WINDOWED = { w: 680, h: 760 };

// feet per side from the 4 corners ([SW,SE,NE,NW])
const sidesFeet = (p: Pt[], sc: number) => ({
  n: dist(p[3], p[2]) / sc, // north / top   (NW-NE)
  e: dist(p[2], p[1]) / sc, // east  / right (NE-SE)
  s: dist(p[0], p[1]) / sc, // south / bottom(SW-SE)
  w: dist(p[3], p[0]) / sc, // west  / left  (NW-SW)
});

export const House2Editor: React.FC<Props> = ({ open, getIframe, onClose, initialPreset, onPresetApplied }) => {
  const figRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const snapRef = useRef<Snap | null>(null);
  const didInitZoom = useRef(false);
  const [zoom, setZoom] = useState(1); // multiplier on the fit-to-box scale
  // House elements (rooms placed on the plot).
  const [rooms, setRooms] = useState<FPRoom[]>([]);
  const roomsRef = useRef<FPRoom[]>([]);
  const [selRoom, setSelRoom] = useState<string | null>(null);
  const [selIds, setSelIds] = useState<string[]>([]);   // multi-selection (shift+click)
  const [scaleMode, setScaleMode] = useState(false); // group-scale the whole house
  const historyRef = useRef<FPRoom[][]>([]);           // undo stack (rooms snapshots)
  const futureRef = useRef<FPRoom[][]>([]);            // redo stack
  const [toolbarRoom, setToolbarRoom] = useState<string | null>(null); // room whose toolbar is open (double-click)
  const [drawMode, setDrawMode] = useState(false);
  const [drawTool, setDrawTool] = useState<'shape' | 'line'>('shape'); // drag-a-shape vs click-to-connect polygon
  const [drawRect, setDrawRect] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [polyPts, setPolyPts] = useState<Pt[]>([]); // in-progress polygon vertices
  const [curType, setCurType] = useState('rect');
  const [panelTab, setPanelTab] = useState<'elements' | 'draw' | 'preset'>('elements');
  const [wall, setWall] = useState<'single' | 'double'>('single');
  const [labels, setLabels] = useState(true);
  const [amalgamate, setAmalgamate] = useState(false); // merge overlapping rooms into one shape
  const [pendingPreset, setPendingPreset] = useState<null | (() => { rooms: FPRoom[]; wall?: 'single' | 'double' })>(null);
  const [showCleanup, setShowCleanup] = useState(false); // prompt to clear centre name / move dims out
  const cleanupPlot = () => {
    getIframe()?.contentWindow?.postMessage({ type: 'SET_FIELDS', fields: { show_plot_name: false, dims_outside: true } }, '*');
    setShowCleanup(false);
    setTimeout(snapshot, 80);
  };
  // Render rooms through the ENGINE (exact: stairs, walls, label fonts) by setting
  // its global + redrawing immediately, then re-snapshot so the popup mirrors it.
  const renderRooms = (rs: FPRoom[], w = wall, lbl = labels, am = amalgamate) => {
    const win: any = getIframe()?.contentWindow;
    if (win) { win.houseLayout = { rooms: rs, wall: w, labels: lbl, amalgamate: am }; try { win.drawMap(); } catch { /* not ready */ } }
    snapshot();
  };
  const applyHouse = (rs: FPRoom[], w = wall, lbl = labels, am = amalgamate) => {
    renderRooms(rs, w, lbl, am);
    // Notify parent so the design persists (FORM_CHANGED → formDataRef).
    getIframe()?.contentWindow?.postMessage({ type: 'SET_HOUSE_LAYOUT', layout: { rooms: rs, wall: w, labels: lbl, amalgamate: am } }, '*');
  };
  const cloneRooms = (rs: FPRoom[]) => rs.map((r) => ({ ...r, points: r.points.map((p) => ({ ...p })), doors: r.doors ? r.doors.map((d) => ({ ...d })) : undefined }));
  const snapshotRooms = () => cloneRooms(roomsRef.current);
  const recordHistory = (before: FPRoom[]) => { historyRef.current.push(before); if (historyRef.current.length > 80) historyRef.current.shift(); futureRef.current = []; };
  const applyRooms = (rs: FPRoom[]) => { roomsRef.current = rs; setRooms(rs); applyHouse(rs); };
  const commitRooms = (rs: FPRoom[]) => { recordHistory(snapshotRooms()); applyRooms(rs); };
  const clearSel = () => { setSelRoom(null); setSelIds([]); setToolbarRoom(null); };
  const undo = () => { if (!historyRef.current.length) return; futureRef.current.push(snapshotRooms()); applyRooms(historyRef.current.pop()!); clearSel(); };
  const redo = () => { if (!futureRef.current.length) return; historyRef.current.push(snapshotRooms()); applyRooms(futureRef.current.pop()!); clearSel(); };
  const deleteSelected = () => {
    const ids = selIds.length ? selIds : (selRoom ? [selRoom] : []);
    if (!ids.length) return;
    commitRooms(roomsRef.current.filter((r) => !ids.includes(r.id)));
    clearSel();
  };
  const toggleSel = (id: string) => setSelIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  // Merge the selected (overlapping) rooms into one shape: they get a shared group
  // id so the engine draws a single label and they move/select/delete together.
  // The first non-empty label becomes the group's label.
  const mergeSelected = () => {
    const ids = selIds.filter((id) => { const r = roomsRef.current.find((x) => x.id === id); return r && r.type !== 'dim'; });
    if (ids.length < 2) return;
    const gid = 'g' + huid();
    const groupLabel = ids.map((id) => roomsRef.current.find((r) => r.id === id)?.label).find((l) => l && l.trim()) || 'ROOM';
    // All members share the group label so editing any of them renames the shape.
    commitRooms(roomsRef.current.map((r) => ids.includes(r.id) ? { ...r, group: gid, label: groupLabel } : r));
  };
  // Set a room's label; if it's merged, rename the whole group together.
  const setRoomLabel = (id: string, value: string) => {
    const gid = roomsRef.current.find((r) => r.id === id)?.group;
    commitRooms(roomsRef.current.map((r) => (r.id === id || (gid && r.group === gid)) ? { ...r, label: value } : r));
  };
  // Split a merged group back into independent rooms.
  const unmergeSelected = () => {
    const ids = selIds.length ? selIds : (selRoom ? [selRoom] : []);
    const groups = new Set(ids.map((id) => roomsRef.current.find((r) => r.id === id)?.group).filter(Boolean));
    if (!groups.size) return;
    commitRooms(roomsRef.current.map((r) => (r.group && groups.has(r.group)) ? { ...r, group: undefined } : r));
  };
  const selHasGroup = () => (selIds.length ? selIds : (selRoom ? [selRoom] : [])).some((id) => roomsRef.current.find((r) => r.id === id)?.group);
  // Multi-select modifier — Shift, plus Cmd (⌘) on Mac / Ctrl on Windows, so it
  // works the same on both platforms.
  const isMultiKey = (e: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) => e.shiftKey || e.metaKey || e.ctrlKey;
  // Topmost element whose bbox contains a normalized point (last drawn = on top).
  const elAt = (nx: number, ny: number) => {
    for (let i = roomsRef.current.length - 1; i >= 0; i--) {
      const r = roomsRef.current[i]; if (!r.points || !r.points.length) continue;
      const xs = r.points.map((p) => p.x), ys = r.points.map((p) => p.y);
      if (nx >= Math.min(...xs) - 0.01 && nx <= Math.max(...xs) + 0.01 && ny >= Math.min(...ys) - 0.01 && ny <= Math.max(...ys) + 0.01) return r.id;
    }
    return null;
  };
  // Ray-cast point-in-polygon (precise for circles/trapeziums/free figures).
  const pointInPoly = (nx: number, ny: number, pts: FPPoint[]) => {
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
      if (((yi > ny) !== (yj > ny)) && (nx < (xj - xi) * (ny - yi) / (yj - yi || 1e-9) + xi)) inside = !inside;
    }
    return inside;
  };
  // Topmost actual figure (not a dimension) the point falls inside.
  const figureAt = (nx: number, ny: number) => {
    for (let i = roomsRef.current.length - 1; i >= 0; i--) {
      const r = roomsRef.current[i]; if (r.type === 'dim' || !r.points || r.points.length < 3) continue;
      if (pointInPoly(nx, ny, r.points)) return r.id;
    }
    return null;
  };
  // Pointer-down on an element: modifier = add/remove from multi-selection; clicking
  // a member of a multi-selection drags the whole set; otherwise select+drag just it.
  const onElPointerDown = (e: React.PointerEvent, id: string) => {
    if (isMultiKey(e)) { e.preventDefault(); e.stopPropagation(); setSelRoom(null); setToolbarRoom(null); toggleSel(id); return; }
    if (selIds.length > 1 && selIds.includes(id)) { startGroupMove(e, selIds); return; }
    // Merged rooms move/select as one: clicking any member grabs the whole group.
    const clicked = roomsRef.current.find((r) => r.id === id);
    if (clicked && clicked.group) {
      const gids = roomsRef.current.filter((r) => r.group === clicked.group).map((r) => r.id);
      if (gids.length > 1) { e.preventDefault(); e.stopPropagation(); setSelIds(gids); setSelRoom(null); setToolbarRoom(null); startGroupMove(e, gids); return; }
    }
    setSelIds([id]); startRoomDrag(e, id);
  };
  const labelFor = (type: string) => {
    if (type === 'stairs') return 'STAIRCASE';
    const n = roomsRef.current.filter((r) => r.type !== 'dim' && r.type !== 'stairs').length + 1;
    return `ROOM ${n}`;
  };
  const addRoom = (type: string) => {
    const off = (roomsRef.current.length % 4) * 0.05;
    const room: FPRoom = { id: huid(), type, label: labelFor(type), points: makeShapePoints(type, 0.28 + off, 0.28 + off, 0.58 + off, 0.58 + off), rect: type === 'rect' || type === 'stairs', ...(type === 'trapezium' ? { trapDir: 'top', trapInset: 0.22 } : {}) };
    commitRooms([...roomsRef.current, room]); setSelRoom(room.id);
  };
  // Add a dimension arrow (a 2-point measuring line, value auto-computed). Placed
  // low in the plot ("outside" the house) by default; drag the ends to extend/aim.
  const addDim = () => {
    const room: FPRoom = { id: huid(), type: 'dim', label: '', points: [{ x: 0.12, y: 0.9 }, { x: 0.62, y: 0.9 }], rect: false };
    commitRooms([...roomsRef.current, room]); setSelRoom(room.id);
  };
  // Build a preset from the shared builder, sized to the current plot.
  const buildPreset = (kind: string): { rooms: FPRoom[]; wall: 'double' } =>
    buildHousePreset(kind, snap ? geomFromCanvas(snap.canvasPts, snap.scale) : null);
  // Apply a preset by replacing or adding to the current layout.
  const applyPreset = (build: () => { rooms: FPRoom[]; wall?: 'single' | 'double' }, mode: 'replace' | 'add') => {
    const layout = build();
    const rs = mode === 'replace' ? layout.rooms : [...roomsRef.current, ...layout.rooms];
    const w = layout.wall || wall;
    recordHistory(snapshotRooms());
    if (layout.wall) setWall(layout.wall);
    roomsRef.current = rs; setRooms(rs); applyHouse(rs, w, labels, amalgamate);
    // Default-select the whole house so it can be moved/scaled right away.
    const ids = rs.map((r) => r.id);
    setSelIds(ids); setSelRoom(ids.length === 1 ? ids[0] : null); setToolbarRoom(null); setScaleMode(false);
    setPendingPreset(null);
  };
  // Tap a preset: confirm first if a drawing already exists, else place it.
  const requestPreset = (build: () => { rooms: FPRoom[]; wall?: 'single' | 'double' }) => {
    if (roomsRef.current.length > 0) setPendingPreset(() => build);
    else applyPreset(build, 'replace');
  };
  // A preset picker card: a mini thumbnail + title (+ optional DOUBLE badge).
  const presetCard = (thumb: React.ReactNode, title: string, sub: string, badge: boolean, onClick: () => void) => (
    <button onClick={onClick} title={title}
      style={{ display: 'flex', flexDirection: 'column', background: '#101113', border: '1px solid #2a2a2a', borderRadius: 10, padding: 8, cursor: 'pointer', textAlign: 'left' }}>
      <div style={{ background: '#0d0d0d', border: '1px solid #232323', borderRadius: 7, overflow: 'hidden', lineHeight: 0 }}>{thumb}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 7, gap: 6 }}>
        <span style={{ color: '#eee', fontSize: 12, fontWeight: 700 }}>{title}</span>
        {badge ? <span style={{ color: '#1a1a1a', background: '#FFD93D', fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 999 }}>DOUBLE</span> : null}
      </div>
      {sub ? <span style={{ color: '#888', fontSize: 10.5, marginTop: 2 }}>{sub}</span> : null}
    </button>
  );
  // Bottom + left dimension ticks + arrowheads for the preset thumbnails.
  const thumbDims = (
    <>
      <g stroke="#141414" strokeWidth={0.9}>
        <line x1="30" y1="64" x2="96" y2="64" /><line x1="30" y1="61" x2="30" y2="67" /><line x1="96" y1="61" x2="96" y2="67" />
        <line x1="20" y1="12" x2="20" y2="56" /><line x1="17" y1="12" x2="23" y2="12" /><line x1="17" y1="56" x2="23" y2="56" />
      </g>
      <path d="M30 64 l4 -2 v4 z M96 64 l-4 -2 v4 z M20 12 l-2 4 h4 z M20 56 l-2 -4 h4 z" fill="#141414" />
    </>
  );
  const deleteRoom = (id: string) => { commitRooms(roomsRef.current.filter((r) => r.id !== id)); setSelRoom(null); setToolbarRoom(null); };
  // Single click only selects; the toolbar (double-click) closes when selection moves.
  useEffect(() => { if (toolbarRoom && toolbarRoom !== selRoom) setToolbarRoom(null); }, [selRoom, toolbarRoom]);
  // Keyboard: Ctrl/Cmd+Z undo, +Shift or Ctrl+Y redo, Del/Backspace delete, Esc clear.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (meta && (e.key === 'y' || e.key === 'Y')) { e.preventDefault(); redo(); }
      else if (meta && (e.key === 'a' || e.key === 'A')) { e.preventDefault(); const ids = roomsRef.current.map((r) => r.id); setSelIds(ids); setSelRoom(ids.length === 1 ? ids[0] : null); setToolbarRoom(null); setScaleMode(false); }
      else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); deleteSelected(); }
      else if (e.key === 'Escape') { clearSel(); setScaleMode(false); setPolyPts([]); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selIds, selRoom]); // eslint-disable-line react-hooks/exhaustive-deps
  // ---- Doors (live in room.doors) ----
  const patchRoom = (id: string, fn: (r: FPRoom) => FPRoom) => commitRooms(roomsRef.current.map((r) => r.id === id ? fn(r) : r));
  // Swap a trapezium to a variation (narrow side + taper) in place, keeping its box.
  const applyTrapVariation = (id: string, dir: string, inset: number) => {
    const room = roomsRef.current.find((r) => r.id === id); if (!room) return;
    const xs = room.points.map((p) => p.x), ys = room.points.map((p) => p.y);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    patchRoom(id, (r) => ({ ...r, trapDir: dir, trapInset: inset, points: makeTrapeziumPoints(x0, y0, x1, y1, dir, inset) }));
  };
  const addDoor = (roomId: string) => patchRoom(roomId, (r) => ({ ...r, doors: [...(r.doors || []), { id: huid(), wall: 2, pos: 0.5, width: 0.26 }] }));
  const deleteDoor = (roomId: string, doorId: string) => patchRoom(roomId, (r) => ({ ...r, doors: (r.doors || []).filter((d) => d.id !== doorId) }));
  const setWall2 = (w: 'single' | 'double') => { setWall(w); applyHouse(roomsRef.current, w, labels, amalgamate); };
  const setLabels2 = (l: boolean) => { setLabels(l); applyHouse(roomsRef.current, wall, l, amalgamate); };
  const setAmalg2 = (v: boolean) => { setAmalgamate(v); applyHouse(roomsRef.current, wall, labels, v); };
  const [fullscreen, setFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 700);
  // Mobile: when locked, the canvas can't pan, so dragging rooms/dimensions moves
  // only the element (no accidental canvas movement). Unlock to pan/zoom the canvas.
  const [canvasLocked, setCanvasLocked] = useState(true);
  const [box, setBox] = useState(WINDOWED); // available canvas box (grows in fullscreen)
  const boxRef = useRef(box);
  useEffect(() => { boxRef.current = box; }, [box]);
  useEffect(() => {
    const calc = () => {
      const mob = window.innerWidth <= 700;
      setIsMobile(mob);
      // Mobile: canvas fills the width; the panel is a bottom sheet (~45vh), so the
      // canvas gets the rest of the height. Desktop: fixed window / fullscreen.
      setBox(mob
        ? { w: Math.max(240, window.innerWidth - 20), h: Math.max(220, Math.round(window.innerHeight * 0.55) - 44) }
        : fullscreen
          ? { w: Math.max(420, window.innerWidth - 300 - 96), h: Math.max(420, window.innerHeight - 170) }
          : WINDOWED);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [fullscreen]);
  const [snap, setSnap] = useState<Snap | null>(null);
  // Apply a preset once when opened from the template picker (after the plot snaps).
  const presetDoneRef = useRef(false);
  useEffect(() => { if (!open) presetDoneRef.current = false; }, [open]);
  useEffect(() => {
    if (open && initialPreset && snap && !presetDoneRef.current) {
      presetDoneRef.current = true;
      applyPreset(() => buildPreset(initialPreset), 'replace');
      onPresetApplied?.();
    }
  }, [open, initialPreset, snap]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pts, setPts] = useState<Pt[]>([]);
  const ptsRef = useRef<Pt[]>([]);
  const [shape, setShape] = useState<Shape>('rectangle');
  const shapeRef = useRef<Shape>('rectangle');
  const dragRef = useRef<number | null>(null);
  const origRef = useRef<{ n: number; s: number; e: number; w: number; shape: Shape } | null>(null);
  // Display px per SVG unit = the document's actual on-screen scale, captured
  // once at open so the popup figure is shown at the SAME size/proportions as
  // it appears in the document (relative ratios stay faithful).
  const kFixedRef = useRef<number>(0);
  const [dims, setDims] = useState<{ n: number; s: number; e: number; w: number }>({ n: 30, s: 30, e: 50, w: 50 });
  const [showName, setShowName] = useState(true);
  const [dimsOutside, setDimsOutside] = useState(false);
  const [roads, setRoads] = useState({ north: false, south: false, east: false, west: false });
  const [origin, setOrigin] = useState('SW'); // trapezoid fixed corner (right-angle axis)
  const [areaYd, setAreaYd] = useState('');
  const [areaMt, setAreaMt] = useState('');
  const [areaOverride, setAreaOverride] = useState(false);
  const [confirmRect, setConfirmRect] = useState(false); // guard trapezoid -> rectangle
  // In-popup element editing (move any text/block, double-click to edit text).
  // Nothing is selected by default — a click selects whatever is under it (the
  // plot shows reshape corners; a text/label/dimension shows a move outline).
  const [sel, setSel] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [selFig, setSelFig] = useState(false);
  const [selRoad, setSelRoad] = useState<string | null>(null); // selected road side (top/bottom/left/right)
  const [selPulse, setSelPulse] = useState<string | null>(null); // selected pulse key "side|0|1"
  const [editing, setEditing] = useState<{ field: string; left: number; top: number; eh: number; value: string; b: boolean; i: boolean; u: boolean; align: string; case: string; sc: boolean; ds: number; lh: number; fs: number } | null>(null);
  const [editPos, setEditPos] = useState<{ left: number; top: number } | null>(null); // dragged editor position (null = auto)
  const [note, setNote] = useState<string | null>(null);
  const noteTimer = useRef<any>(null);
  const flashNote = (msg: string) => { setNote(msg); if (noteTimer.current) clearTimeout(noteTimer.current); noteTimer.current = setTimeout(() => setNote(null), 2600); };

  const setPtsBoth = (next: Pt[]) => { ptsRef.current = next; setPts(next); };
  useEffect(() => { snapRef.current = snap; }, [snap]);
  // Scroll the canvas so the figure is centred at the given zoom.
  const centerOnFigure = (z: number) => {
    const sc = scrollRef.current; const s = snapRef.current; if (!sc || !s) return;
    const fitK = Math.min(boxRef.current.w / s.vbW, boxRef.current.h / s.vbH);
    const k = fitK * z;
    const cx = (s.figCx - s.vbX) * k + 18, cy = (s.figCy - s.vbY) * k + 18;
    sc.scrollLeft = Math.max(0, cx - sc.clientWidth / 2);
    sc.scrollTop = Math.max(0, cy - sc.clientHeight / 2);
  };
  const applyZoom = (z: number) => { const nz = Math.max(0.4, Math.min(6, Math.round(z * 100) / 100)); setZoom(nz); setTimeout(() => centerOnFigure(nz), 50); };
  const fitScreen = () => { setZoom(1); setTimeout(() => { const sc = scrollRef.current; if (sc) { sc.scrollLeft = 0; sc.scrollTop = 0; } }, 50); };
  // Re-frame the figure to the new canvas size when fullscreen/window changes.
  useEffect(() => {
    const s = snapRef.current; if (!s || !didInitZoom.current) return;
    const fitK = Math.min(box.w / s.vbW, box.h / s.vbH);
    const focusK = Math.min(box.w / s.figW, box.h / s.figH);
    const dz = Math.max(1, focusK / fitK);
    setZoom(dz);
    setTimeout(() => centerOnFigure(dz), 90);
  }, [box]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { shapeRef.current = shape; }, [shape]);

  const snapshot = useCallback(() => {
    const ifr = getIframe(); if (!ifr) return;
    const win: any = ifr.contentWindow; const doc = ifr.contentDocument; if (!win || !doc) return;
    const g = win.__plotGeom; const svgEl = doc.getElementById('mapSvg') as any;
    if (!g || !g.canvasPts || !svgEl) return;

    // Show the WHOLE document page (so the figure sits in real context), but
    // render only the figure: keep the plot, dimensions, road lines + the page
    // frame, and hide every other element (header, references, witnesses,
    // signatures, compass, watermark). Side/road labels are kept (data-plotfig).
    const vb = (svgEl.getAttribute('viewBox') || '0 0 793 1122').split(/\s+/).map(Number);
    const vbX = vb[0] || 0, vbY = vb[1] || 0, vbW = vb[2] || 793, vbH = vb[3] || 1122;

    const clone = svgEl.cloneNode(true) as SVGElement;
    clone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
    const hide = (el: Element | null) => el && el.setAttribute('style', ((el.getAttribute('style') || '') + ';display:none'));
    hide(clone.querySelector('#layer-watermark'));
    // Fade the PLOT layers to a light reference, but keep the engine-drawn house
    // rooms crisp + full by lifting them into a top group (so the popup matches
    // the document exactly — stairs, walls, label fonts and all).
    ['#layer-plot', '#layer-dimensions', '#layer-border', '#layer-text'].forEach((sel) => {
      const lay = clone.querySelector(sel);
      if (lay) lay.setAttribute('style', ((lay.getAttribute('style') || '') + ';opacity:0.26'));
    });
    const houseTop = svgEl.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'g');
    houseTop.setAttribute('id', 'house-top');
    Array.prototype.forEach.call(clone.querySelectorAll('[data-house]'), (el: Element) => { el.setAttribute('style', ''); houseTop.appendChild(el); });
    clone.appendChild(houseTop);

    const cps: Pt[] = g.canvasPts.map((p: any) => ({ x: p.x, y: p.y }));
    // Figure bbox (plot quad) + generous margin → used to frame/zoom by default.
    const xs = cps.map((p) => p.x), ys = cps.map((p) => p.y);
    const fx0 = Math.min(...xs), fx1 = Math.max(...xs), fy0 = Math.min(...ys), fy1 = Math.max(...ys);
    const mx = (fx1 - fx0) * 0.6, my = (fy1 - fy0) * 0.6;
    const figW = (fx1 - fx0) + mx * 2, figH = (fy1 - fy0) + my * 2;
    const figCx = (fx0 + fx1) / 2, figCy = (fy0 + fy1) / 2;
    setSnap({ canvasPts: cps, scale: g.scale || 1, vbX, vbY, vbW, vbH, svg: clone, roads: g.roads || {}, figCx, figCy, figW, figH });
    setPtsBoth(cps);
    // Default zoom: frame the figure with margin (once per open).
    if (!didInitZoom.current) {
      didInitZoom.current = true;
      const fitK = Math.min(boxRef.current.w / vbW, boxRef.current.h / vbH);
      const focusK = Math.min(boxRef.current.w / figW, boxRef.current.h / figH);
      const dz = Math.max(1, focusK / fitK);
      setZoom(dz);
      setTimeout(() => centerOnFigure(dz), 60);
    }
  }, [getIframe]);

  useEffect(() => {
    if (!open) { setSnap(null); didInitZoom.current = false; return; }
    didInitZoom.current = false;
    setFullscreen(false);
    const ifr = getIframe(); if (!ifr) return;
    const win: any = ifr.contentWindow; const doc = ifr.contentDocument;
    // Load existing house rooms.
    const hl = win.houseLayout;
    const rs = (hl && hl.rooms) ? JSON.parse(JSON.stringify(hl.rooms)) : [];
    roomsRef.current = rs; setRooms(rs); setSelRoom(null); setDrawMode(false);
    setWall(hl && hl.wall === 'double' ? 'double' : 'single'); setLabels(hl ? hl.labels !== false : true); setAmalgamate(!!(hl && hl.amalgamate));
    const val = (id: string) => parseFloat((doc?.getElementById(id) as HTMLInputElement | null)?.value || '') || 0;
    const ft = (f: string, i: string) => val(f) + val(i) / 12;
    const d = { n: ft('n_ft', 'n_in'), s: ft('s_ft', 's_in'), e: ft('e_ft', 'e_in'), w: ft('w_ft', 'w_in') };
    const sh: Shape = (Math.abs(d.n - d.s) > 0.05 || Math.abs(d.e - d.w) > 0.05) ? 'trapezoid' : 'rectangle';
    setShape(sh); shapeRef.current = sh;
    origRef.current = { ...d, shape: sh };
    // Read the current toggles from the engine's form.
    const chk = (id: string) => !!(doc?.getElementById(id) as HTMLInputElement | null)?.checked;
    const nameShown = doc?.getElementById('show_plot_name') ? chk('show_plot_name') : true;
    const dimsIn = !chk('dims_outside');
    setShowName(nameShown);
    setDimsOutside(!dimsIn);
    setShowCleanup(nameShown || dimsIn); // they overlap the house area → offer cleanup
    setRoads({ north: chk('road_north'), south: chk('road_south'), east: chk('road_east'), west: chk('road_west') });
    setOrigin((doc?.getElementById('plot_origin') as HTMLSelectElement | null)?.value || 'SW');
    var ydEl = doc?.getElementById('area_sqyd_override') as HTMLInputElement | null;
    var mtEl = doc?.getElementById('area_sqm_override') as HTMLInputElement | null;
    var isOv = !!(ydEl && ydEl.dataset.manualOverride === '1') || !!(mtEl && mtEl.dataset.manualOverride === '1');
    setAreaOverride(isOv);
    setAreaYd(isOv && ydEl ? (ydEl.value || '') : '');
    setAreaMt(isOv && mtEl ? (mtEl.value || '') : '');
    setConfirmRect(false);
    setSel(null); setSelFig(false); setSelRoad(null); setSelPulse(null); setEditing(null);
    // Capture the document's actual on-screen scale (px per SVG unit) so the
    // popup shows the figure at exactly that size.
    const svgEl: any = doc?.getElementById('mapSvg');
    const ctm = svgEl?.getScreenCTM?.();
    kFixedRef.current = (ctm && ctm.a) || 0;
    void win;
    snapshot();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mount / replace the cloned figure inside the preview box.
  useEffect(() => {
    const c = figRef.current; if (!c) return;
    c.replaceChildren();
    if (snap) {
      const fit = Math.min(box.w / snap.vbW, box.h / snap.vbH);
      const k = fit * zoom;
      // Plot is a light, non-editable reference in the House editor.
      snap.svg.setAttribute('style', `width:${Math.round(snap.vbW * k)}px;height:${Math.round(snap.vbH * k)}px;display:block;background:#fff;overflow:hidden;`);
      c.appendChild(snap.svg);
    }
  }, [snap, zoom, box]);

  // Keep the dimension inputs in sync with the actual figure after each change.
  useEffect(() => {
    if (!snap) return;
    const f = sidesFeet(snap.canvasPts, snap.scale);
    setDims({ n: r1(f.n), s: r1(f.s), e: r1(f.e), w: r1(f.w) });
  }, [snap]);

  if (!open) return null;

  const fit = snap ? Math.min(box.w / snap.vbW, box.h / snap.vbH) : 1;
  const k = snap ? fit * zoom : 1;
  const dW = snap ? Math.round(snap.vbW * k) : box.w;
  const dH = snap ? Math.round(snap.vbH * k) : box.h;
  const toPx = (p: Pt) => ({ x: (p.x - (snap?.vbX || 0)) * k, y: (p.y - (snap?.vbY || 0)) * k });

  // ---- House element mapping (normalized plot space <-> canvas px) ----
  const HM = 0.05;
  // houseMap insets normalized [0,1] by HM, so [0,1] never reaches the plot edge.
  // These are the normalized values that map exactly to the plot border, so the
  // house can be moved/scaled all the way up to the edge.
  const N_LO = -HM / (1 - 2 * HM);
  const N_HI = (1 - HM) / (1 - 2 * HM);
  const clampN = (v: number) => Math.max(N_LO, Math.min(N_HI, v));
  // Rooms map into the plot's axis-aligned bbox (matches the engine houseMap) so
  // they stay rectangular on irregular plots.
  const normToPx = (nx: number, ny: number) => {
    const cp = snap!.canvasPts; const xs = cp.map((p) => p.x), ys = cp.map((p) => p.y);
    const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
    const u = HM + nx * (1 - 2 * HM), v = HM + ny * (1 - 2 * HM);
    return toPx({ x: minx + u * (maxx - minx), y: miny + v * (maxy - miny) });
  };
  const pxToNorm = (clientX: number, clientY: number) => {
    const cont = figRef.current!; const r = cont.getBoundingClientRect();
    const sx = (clientX - r.left) / k + snap!.vbX, sy = (clientY - r.top) / k + snap!.vbY;
    const cp = snap!.canvasPts; const xs = cp.map((p) => p.x), ys = cp.map((p) => p.y);
    const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
    const u = (sx - minx) / ((maxx - minx) || 1), v = (sy - miny) / ((maxy - miny) || 1);
    return { x: clampN((u - HM) / (1 - 2 * HM)), y: clampN((v - HM) / (1 - 2 * HM)) };
  };
  // Drag a whole room (shift all its points in normalized space). Renders live
  // through the engine (rAF-throttled) so what you drag matches the document.
  const startRoomDrag = (e: React.PointerEvent, id: string) => {
    e.preventDefault(); e.stopPropagation(); setSelRoom(id);
    const room = roomsRef.current.find((r) => r.id === id); if (!room) return;
    const before = snapshotRooms();
    const base = room.points.map((p) => ({ ...p }));
    const bx0 = Math.min(...base.map((p) => p.x)), bx1 = Math.max(...base.map((p) => p.x));
    const by0 = Math.min(...base.map((p) => p.y)), by1 = Math.max(...base.map((p) => p.y));
    // Other rooms' edge boxes to snap against (plus the plot walls 0 / 1).
    const movingIsStair = room.type === 'stairs';
    const nbrs = roomsRef.current.filter((r) => r.id !== id && r.type !== 'dim' && r.points && r.points.length).map((r) => {
      const rxs = r.points.map((p) => p.x), rys = r.points.map((p) => p.y);
      return { minX: Math.min(...rxs), maxX: Math.max(...rxs), minY: Math.min(...rys), maxY: Math.max(...rys), stair: r.type === 'stairs' };
    });
    // Double-wall thickness in normalized units. Two double-wall ROOMS snap to
    // overlap by this so their walls sit on top of each other (one shared wall).
    // A stair (single line) never overlaps — it snaps flush to the neighbour's
    // outer edge. WALL_SVG must match the engine's _insetPoly gap.
    const cp2 = snap!.canvasPts; const cxs = cp2.map((p) => p.x), cys = cp2.map((p) => p.y);
    const bboxW = (Math.max(...cxs) - Math.min(...cxs)) * (1 - 2 * HM) || 1;
    const bboxH = (Math.max(...cys) - Math.min(...cys)) * (1 - 2 * HM) || 1;
    const WALL_SVG = 3.2;
    const ovX0 = wall === 'double' ? WALL_SVG / bboxW : 0;
    const ovY0 = wall === 'double' ? WALL_SVG / bboxH : 0;
    const SNAP = 0.02; // normalized; ~2% of plot width
    const start = pxToNorm(e.clientX, e.clientY); let moved = false, raf = 0;
    const move = (ev: PointerEvent) => {
      const n = pxToNorm(ev.clientX, ev.clientY); let dx = n.x - start.x, dy = n.y - start.y;
      if (!moved && Math.abs(dx) + Math.abs(dy) < 0.002) return; moved = true;
      // Snap edges. Opposing edges (my right ↔ their left) overlap by the wall
      // thickness so double walls merge into one; a stair (either side) snaps flush
      // so its single line lands on the neighbour's outer edge. Same-side edges and
      // plot walls always align flush.
      let aX = 0, dX = SNAP;
      const takeX = (movingPos: number, target: number) => { const d = Math.abs(movingPos - target); if (d < dX) { dX = d; aX = target - movingPos; } };
      const lM = bx0 + dx, rM = bx1 + dx;
      takeX(lM, 0); takeX(rM, 0); takeX(lM, 1); takeX(rM, 1);
      nbrs.forEach((nb) => {
        const ox = (movingIsStair || nb.stair) ? 0 : ovX0;
        takeX(rM, nb.minX + ox);  // my right edge → their left (overlap unless a stair)
        takeX(lM, nb.maxX - ox);  // my left edge → their right
        takeX(lM, nb.minX);       // align left edges (flush)
        takeX(rM, nb.maxX);       // align right edges (flush)
      });
      let aY = 0, dY = SNAP;
      const takeY = (movingPos: number, target: number) => { const d = Math.abs(movingPos - target); if (d < dY) { dY = d; aY = target - movingPos; } };
      const tM = by0 + dy, bM = by1 + dy;
      takeY(tM, 0); takeY(bM, 0); takeY(tM, 1); takeY(bM, 1);
      nbrs.forEach((nb) => {
        const oy = (movingIsStair || nb.stair) ? 0 : ovY0;
        takeY(bM, nb.minY + oy);  // my bottom → their top
        takeY(tM, nb.maxY - oy);  // my top → their bottom
        takeY(tM, nb.minY);       // align top edges (flush)
        takeY(bM, nb.maxY);       // align bottom edges (flush)
      });
      dx += aX; dy += aY;
      const next = roomsRef.current.map((r) => r.id === id ? { ...r, points: base.map((p) => ({ x: clampN(p.x + dx), y: clampN(p.y + dy) })) } : r);
      roomsRef.current = next; setRooms(next);
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; renderRooms(roomsRef.current); });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) cancelAnimationFrame(raf); if (moved) { recordHistory(before); applyHouse(roomsRef.current); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Resize a room by dragging a corner; opposite corner stays fixed (rect kept
  // axis-aligned in normalized space, so it never skews on irregular plots).
  const startRoomResize = (e: React.PointerEvent, id: string, corner: number) => {
    e.preventDefault(); e.stopPropagation(); setSelRoom(id);
    const room = roomsRef.current.find((r) => r.id === id); if (!room) return;
    const xs = room.points.map((p) => p.x), ys = room.points.map((p) => p.y);
    // Original bbox (kept fixed at the opposite corner); the shape's points are
    // remapped into the new bbox so circles/trapeziums keep their shape.
    const obx0 = Math.min(...xs), obx1 = Math.max(...xs), oby0 = Math.min(...ys), oby1 = Math.max(...ys);
    const obw = obx1 - obx0 || 1e-6, obh = oby1 - oby0 || 1e-6;
    const basePts = room.points.map((p) => ({ ...p }));
    const grab = { x: corner === 1 || corner === 2 ? obx1 : obx0, y: corner >= 2 ? oby1 : oby0 };
    const fixed = { x: grab.x === obx1 ? obx0 : obx1, y: grab.y === oby1 ? oby0 : oby1 };
    const before = snapshotRooms(); let dirty = false, raf = 0;
    const move = (ev: PointerEvent) => {
      dirty = true;
      const n = pxToNorm(ev.clientX, ev.clientY);
      let x0 = Math.min(fixed.x, n.x), x1 = Math.max(fixed.x, n.x), y0 = Math.min(fixed.y, n.y), y1 = Math.max(fixed.y, n.y);
      if (x1 - x0 < 0.03) x1 = x0 + 0.03; if (y1 - y0 < 0.03) y1 = y0 + 0.03;
      const pts = basePts.map((p) => ({ x: x0 + ((p.x - obx0) / obw) * (x1 - x0), y: y0 + ((p.y - oby0) / obh) * (y1 - y0) }));
      const next = roomsRef.current.map((r) => r.id === id ? { ...r, points: pts } : r);
      roomsRef.current = next; setRooms(next);
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; renderRooms(roomsRef.current); });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) cancelAnimationFrame(raf); if (dirty) { recordHistory(before); applyHouse(roomsRef.current); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Drag one end of a dimension arrow (extend / re-aim). The other end stays put.
  const startDimEndDrag = (e: React.PointerEvent, id: string, endIdx: number) => {
    e.preventDefault(); e.stopPropagation(); setSelRoom(id);
    const room0 = roomsRef.current.find((r) => r.id === id); if (!room0) return;
    const s = pxToNorm(e.clientX, e.clientY); const p0 = room0.points[endIdx];
    const gx = p0.x - s.x, gy = p0.y - s.y; // preserve grab offset (grip sits outside the tip → no jump)
    const before = snapshotRooms(); let dirty = false, raf = 0;
    const move = (ev: PointerEvent) => {
      dirty = true;
      const n = pxToNorm(ev.clientX, ev.clientY);
      const np = { x: clampN(n.x + gx), y: clampN(n.y + gy) };
      const next = roomsRef.current.map((r) => r.id === id ? { ...r, points: r.points.map((p, i) => i === endIdx ? np : p) } : r);
      roomsRef.current = next; setRooms(next);
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; renderRooms(roomsRef.current); });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) cancelAnimationFrame(raf); if (dirty) { recordHistory(before); applyHouse(roomsRef.current); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Bounding box of a set of elements (rooms + dimensions) in normalized space.
  // Pass ids for a subset (multi-selection); omit for the whole house.
  const houseBounds = (ids?: string[]) => {
    const rs = roomsRef.current.filter((r) => r.points && r.points.length && (!ids || ids.includes(r.id)));
    if (!rs.length) return null;
    let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
    rs.forEach((r) => r.points.forEach((p) => { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }));
    return { x0, y0, x1, y1 };
  };
  // Drag a group corner to scale a set of elements uniformly about the opposite
  // corner (aspect locked), clamped so it stays inside the plot.
  const startGroupScale = (e: React.PointerEvent, corner: number, ids?: string[]) => {
    e.preventDefault(); e.stopPropagation();
    const b = houseBounds(ids); if (!b) return;
    const corners = [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }];
    const anchor = corners[(corner + 2) % 4], grab = corners[corner];
    const gx = grab.x - anchor.x, gy = grab.y - anchor.y;   // grabbed corner offset from anchor
    const d2 = gx * gx + gy * gy || 1e-9;
    const base = roomsRef.current.filter((r) => !ids || ids.includes(r.id)).map((r) => ({ id: r.id, pts: r.points.map((p) => ({ ...p })) }));
    // Largest factor that keeps every edge of the group bbox within the plot.
    const lim = (edge: number, a: number) => { const d = edge - a; return d > 1e-9 ? (N_HI - a) / d : d < -1e-9 ? (N_LO - a) / d : Infinity; };
    const fMax = Math.min(lim(b.x0, anchor.x), lim(b.x1, anchor.x), lim(b.y0, anchor.y), lim(b.y1, anchor.y));
    const before = snapshotRooms(); let dirty = false, raf = 0;
    const move = (ev: PointerEvent) => {
      dirty = true;
      const n = pxToNorm(ev.clientX, ev.clientY);
      // Aspect-locked: project the pointer onto the anchor→corner diagonal.
      let f = ((n.x - anchor.x) * gx + (n.y - anchor.y) * gy) / d2;
      f = Math.max(0.2, Math.min(f, fMax));
      const next = roomsRef.current.map((r) => {
        const bp = base.find((x) => x.id === r.id); if (!bp) return r;
        return { ...r, points: bp.pts.map((p) => ({ x: clampN(anchor.x + (p.x - anchor.x) * f), y: clampN(anchor.y + (p.y - anchor.y) * f) })) };
      });
      roomsRef.current = next; setRooms(next);
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; renderRooms(roomsRef.current); });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) cancelAnimationFrame(raf); if (dirty) { recordHistory(before); applyHouse(roomsRef.current); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Drag the group box to MOVE a set of elements together (whole house or selection).
  const startGroupMove = (e: React.PointerEvent, ids?: string[]) => {
    e.preventDefault(); e.stopPropagation();
    const b = houseBounds(ids); if (!b) return;
    const base = roomsRef.current.filter((r) => !ids || ids.includes(r.id)).map((r) => ({ id: r.id, pts: r.points.map((p) => ({ ...p })) }));
    const start = pxToNorm(e.clientX, e.clientY);
    const before = snapshotRooms(); let dirty = false, raf = 0;
    const move = (ev: PointerEvent) => {
      dirty = true;
      const n = pxToNorm(ev.clientX, ev.clientY);
      // Clamp translation so the whole group stays within the plot (up to the edge).
      const dx = Math.max(N_LO - b.x0, Math.min(n.x - start.x, N_HI - b.x1));
      const dy = Math.max(N_LO - b.y0, Math.min(n.y - start.y, N_HI - b.y1));
      const next = roomsRef.current.map((r) => {
        const bp = base.find((x) => x.id === r.id); if (!bp) return r;
        return { ...r, points: bp.pts.map((p) => ({ x: p.x + dx, y: p.y + dy })) };
      });
      roomsRef.current = next; setRooms(next);
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; renderRooms(roomsRef.current); });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) cancelAnimationFrame(raf); if (dirty) { recordHistory(before); applyHouse(roomsRef.current); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Centre of a door (normalized), given its room + wall + pos.
  const doorCenterNorm = (room: FPRoom, d: FPDoor): FPPoint => {
    const xs = room.points.map((p) => p.x), ys = room.points.map((p) => p.y);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    if (d.wall === 0) return { x: x0 + d.pos * (x1 - x0), y: y0 };
    if (d.wall === 2) return { x: x0 + d.pos * (x1 - x0), y: y1 };
    if (d.wall === 1) return { x: x1, y: y0 + d.pos * (y1 - y0) };
    return { x: x0, y: y0 + d.pos * (y1 - y0) };
  };
  // Drag a door: it sticks to the nearest wall and slides along it (live).
  const startDoorDrag = (e: React.PointerEvent, roomId: string, doorId: string) => {
    e.preventDefault(); e.stopPropagation(); setSelRoom(roomId);
    const room0 = roomsRef.current.find((r) => r.id === roomId); if (!room0) return;
    let raf = 0;
    const move = (ev: PointerEvent) => {
      const n = pxToNorm(ev.clientX, ev.clientY);
      const room = roomsRef.current.find((r) => r.id === roomId); if (!room) return;
      const xs = room.points.map((p) => p.x), ys = room.points.map((p) => p.y);
      const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
      const dT = Math.abs(n.y - y0), dB = Math.abs(n.y - y1), dL = Math.abs(n.x - x0), dR = Math.abs(n.x - x1);
      const m = Math.min(dT, dB, dL, dR);
      let wall = 2, pos = 0.5;
      if (m === dT) { wall = 0; pos = (n.x - x0) / ((x1 - x0) || 1); }
      else if (m === dB) { wall = 2; pos = (n.x - x0) / ((x1 - x0) || 1); }
      else if (m === dL) { wall = 3; pos = (n.y - y0) / ((y1 - y0) || 1); }
      else { wall = 1; pos = (n.y - y0) / ((y1 - y0) || 1); }
      const w = (room.doors || []).find((d) => d.id === doorId)?.width || 0.26;
      pos = Math.max(w / 2, Math.min(1 - w / 2, pos));
      const next = roomsRef.current.map((r) => r.id === roomId ? { ...r, doors: (r.doors || []).map((d) => d.id === doorId ? { ...d, wall, pos } : d) } : r);
      roomsRef.current = next; setRooms(next);
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; renderRooms(roomsRef.current); });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) cancelAnimationFrame(raf); applyHouse(roomsRef.current); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Free-draw a rectangle room on empty canvas.
  // Line/polygon tool: each click drops a vertex; clicking the first dot (or Finish)
  // closes the figure into a room.
  const finishPoly = () => {
    if (polyPts.length >= 3) {
      const room: FPRoom = { id: huid(), type: 'poly', label: labelFor('poly'), points: polyPts.map((p) => ({ ...p })), rect: false };
      commitRooms([...roomsRef.current, room]); setSelRoom(room.id);
    }
    setPolyPts([]);
  };
  const onLineClick = (e: React.PointerEvent) => {
    const p = pxToNorm(e.clientX, e.clientY);
    if (polyPts.length >= 3) {
      const f = normToPx(polyPts[0].x, polyPts[0].y), cur = normToPx(p.x, p.y);
      if (Math.hypot(cur.x - f.x, cur.y - f.y) < 14) { finishPoly(); return; } // clicked first dot → close
    }
    setPolyPts((c) => [...c, p]);
  };
  const onCanvasPointerDown = (e: React.PointerEvent) => {
    if (!drawMode || !snap) return;
    e.preventDefault();
    if (drawTool === 'line') { e.stopPropagation(); onLineClick(e); return; }
    const a = pxToNorm(e.clientX, e.clientY);
    // Inside an existing figure → select / drag it. Only draw a new shape on empty space.
    const hit = figureAt(a.x, a.y);
    if (hit) { onElPointerDown(e, hit); return; }
    const move = (ev: PointerEvent) => { const b = pxToNorm(ev.clientX, ev.clientY); setDrawRect({ x0: a.x, y0: a.y, x1: b.x, y1: b.y }); };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const b = pxToNorm(ev.clientX, ev.clientY); setDrawRect(null);
      if (Math.abs(b.x - a.x) > 0.03 && Math.abs(b.y - a.y) > 0.03) {
        const room: FPRoom = { id: huid(), type: curType, label: labelFor(curType), points: makeShapePoints(curType, a.x, a.y, b.x, b.y), rect: curType === 'rect' || curType === 'stairs' };
        commitRooms([...roomsRef.current, room]); setSelRoom(room.id);
      }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // Current (calculated or overridden) area values shown read-only when not editing.
  const _adoc = getIframe()?.contentDocument;
  const liveArea = {
    yd: (_adoc?.getElementById('area_sqyd_override') as HTMLInputElement | null)?.value || '',
    mt: (_adoc?.getElementById('area_sqm_override') as HTMLInputElement | null)?.value || '',
  };

  const applyToEngine = (next: Pt[], draggedIdx: number) => {
    if (!snap) return;
    const f = sidesFeet(next, snap.scale);
    const anchor = ANCHOR_FOR[(draggedIdx + 2) % 4];
    const ifr = getIframe(); const win = ifr?.contentWindow;
    win?.postMessage({ type: 'SET_BOUNDARIES', shape: shapeRef.current, n: r1(f.n), s: r1(f.s), e: r1(f.e), w: r1(f.w), anchor }, '*');
    setTimeout(snapshot, 80);
  };

  const startDrag = (e: React.PointerEvent, i: number) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = i;
    const move = (ev: PointerEvent) => {
      const c = figRef.current; if (!c) return;
      const rect = c.getBoundingClientRect();
      const sx = (ev.clientX - rect.left) / k + (snap?.vbX || 0), sy = (ev.clientY - rect.top) / k + (snap?.vbY || 0);
      const prev = ptsRef.current;
      if (shapeRef.current === 'rectangle') {
        const opp = prev[(i + 2) % 4];
        const x0 = Math.min(opp.x, sx), x1 = Math.max(opp.x, sx), y0 = Math.min(opp.y, sy), y1 = Math.max(opp.y, sy);
        // order [SW, SE, NE, NW]
        setPtsBoth([{ x: x0, y: y1 }, { x: x1, y: y1 }, { x: x1, y: y0 }, { x: x0, y: y0 }]);
      } else {
        setPtsBoth(prev.map((p, idx) => idx === i ? { x: sx, y: sy } : p));
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const idx = dragRef.current; dragRef.current = null;
      if (idx != null) applyToEngine(ptsRef.current, idx);
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  // ---- In-popup element editing: move any text/block + double-click to edit ----
  // Inverse of the element's screen-CTM linear part: maps a screen-pixel delta
  // into the element's own coordinate frame (so rotated text moves correctly and
  // the display scale is handled in one step).
  // Resolve the data-field element under a click — falls back to the inner field
  // of a data-plotfig label group (side/road labels) so the whole group works.
  const fieldElFrom = (t: Element): Element | null => {
    const direct = t.closest && t.closest('[data-field]');
    if (direct) return direct;
    const grp = t.closest && t.closest('[data-plotfig]');
    return grp ? grp.querySelector('[data-field]') : null;
  };
  // Robust fallback: SVG text only catches clicks on the painted glyphs, so a
  // thin/rotated label is easily missed. Find the smallest data-field whose
  // screen box contains the pointer.
  const pickField = (cx: number, cy: number): Element | null => {
    const cont = figRef.current; if (!cont) return null;
    let best: Element | null = null, bestArea = Infinity;
    cont.querySelectorAll('[data-field]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) return;
      if (cx >= r.left - 3 && cx <= r.right + 3 && cy >= r.top - 3 && cy <= r.bottom + 3) {
        const a = r.width * r.height;
        if (a < bestArea) { bestArea = a; best = el; }
      }
    });
    return best;
  };
  const invLinearOf = (el: Element | null) => {
    const m = (el as any)?.getScreenCTM?.();
    if (!m) return { a: 1 / k, b: 0, c: 0, d: 1 / k };
    const det = m.a * m.d - m.b * m.c || 1;
    return { a: m.d / det, b: -m.b / det, c: -m.c / det, d: m.a / det };
  };
  const setOffsetAbs = (kind: 'field' | 'block' | 'figure', payload: string, base: { x: number; y: number }, lx: number, ly: number) => {
    const win: any = getIframe()?.contentWindow; if (!win) return;
    if (kind === 'field') { const T = win.textOverrides || (win.textOverrides = {}); if (!T[payload]) T[payload] = { dx: 0, dy: 0, ds: 0, dw: 0 }; T[payload].dx = base.x + lx; T[payload].dy = base.y + ly; }
    else if (kind === 'block') { const key = payload === 'references' ? 'area' : payload; const B = win.blockOffsets || (win.blockOffsets = {}); if (!B[key]) B[key] = { x: 0, y: 0, s: 0 }; B[key].x = base.x + lx; B[key].y = base.y + ly; }
    else if (win.plotPosOffset) { win.plotPosOffset.x = base.x + lx; win.plotPosOffset.y = base.y + ly; }
    try { win.drawMap(); } catch (e) { /* ignore */ }
  };
  const onFigPointerDown = (e: React.PointerEvent) => {
    if (!snap) return;
    // Clicking the figure while an editor is open saves it first.
    if (editing) { commitEdit(editRef.current ? editRef.current.value : editing.value); }
    const win: any = getIframe()?.contentWindow; if (!win) return;
    const tEl = e.target as Element;
    // Pulse (V-mark) is its own element — select on click, drag to move.
    const puEl = tEl.closest && tEl.closest('[data-pulse]');
    if (puEl) {
      const key = puEl.getAttribute('data-pulse') || '';
      const sx = e.clientX, sy = e.clientY; let moved = false;
      const mv = (ev: PointerEvent) => { if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 3) return; moved = true; };
      const up2 = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up2); if (!moved) { setSelPulse(key); setSel(null); setSelFig(false); setSelRoad(null); } };
      window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up2);
      startPulseDrag(e, key, false);
      return;
    }
    const bEl = tEl.closest && tEl.closest('[data-block]');
    const fEl = fieldElFrom(tEl) || (!bEl ? pickField(e.clientX, e.clientY) : null);
    // Click anywhere on a road band (lines / between road & plot) selects it.
    if (!bEl && !fEl) {
      const rh = roadHitTest(e.clientX, e.clientY);
      if (rh) {
        const sx = e.clientX, sy = e.clientY; let moved = false;
        const mv = (ev: PointerEvent) => { if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 3) return; moved = true; };
        const up2 = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up2); if (!moved) { setSelRoad(rh); setSel(null); setSelFig(false); setSelPulse(null); } };
        window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up2);
        return;
      }
    }
    let kind: 'field' | 'block' | 'figure', payload = '', rectEl: Element | null = null;
    if (bEl) { kind = 'block'; payload = bEl.getAttribute('data-block') || ''; rectEl = bEl; }
    else if (fEl) { kind = 'field'; payload = fEl.getAttribute('data-field') || ''; rectEl = fEl; }
    else { kind = 'figure'; }
    const cont = figRef.current; if (!cont) return;
    const cr = cont.getBoundingClientRect();
    const selRect = rectEl ? (() => { const r = rectEl!.getBoundingClientRect(); return { left: r.left - cr.left, top: r.top - cr.top, width: r.width, height: r.height }; })() : null;
    // Capture base engine offset + the (constant) rotation/scale frame once.
    let base = { x: 0, y: 0 };
    if (kind === 'field') { const o = win.textOverrides?.[payload]; base = { x: o?.dx || 0, y: o?.dy || 0 }; }
    else if (kind === 'block') { const key = payload === 'references' ? 'area' : payload; const o = win.blockOffsets?.[key]; base = { x: o?.x || 0, y: o?.y || 0 }; }
    else { base = { x: win.plotPosOffset?.x || 0, y: win.plotPosOffset?.y || 0 }; }
    const inv = invLinearOf(rectEl || cont.querySelector('svg'));
    const sx = e.clientX, sy = e.clientY; let moved = false, raf = 0, ldx = 0, ldy = 0;
    const flush = () => { raf = 0; const lx = inv.a * ldx + inv.c * ldy, ly = inv.b * ldx + inv.d * ldy; setOffsetAbs(kind, payload, base, lx, ly); snapshot(); };
    const move = (ev: PointerEvent) => {
      ldx = ev.clientX - sx; ldy = ev.clientY - sy;
      if (!moved) { if (Math.abs(ldx) + Math.abs(ldy) < 3) return; moved = true; setSel(null); }
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const roadSide = kind === 'field' ? roadSideForField(payload) : null;
    const isRoad = !!(roadSide && snap.roads && snap.roads[roadSide]);
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      setSelPulse(null);
      if (moved) { if (raf) { cancelAnimationFrame(raf); flush(); } if (kind === 'figure') { setSelFig(true); setSelRoad(null); } else if (isRoad) setSelRoad(roadSide); }
      else if (kind === 'figure') { setSelFig(true); setSel(null); setSelRoad(null); }
      else if (isRoad) { setSelRoad(roadSide); setSel(null); setSelFig(false); }
      else if (selRect) { setSel(selRect); setSelFig(false); setSelRoad(null); }
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Hit-test a click against each road's band (between near & far lines) so the
  // road can be selected by clicking anywhere on it, not just its label.
  const roadHitTest = (cx: number, cy: number): string | null => {
    if (!snap || !snap.roads) return null;
    const cont = figRef.current; if (!cont) return null;
    const r = cont.getBoundingClientRect();
    const px = (cx - r.left) / k + (snap.vbX || 0), py = (cy - r.top) / k + (snap.vbY || 0);
    const inPoly = (poly: Pt[]) => {
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
        if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    };
    for (const side of Object.keys(snap.roads)) {
      const rd = snap.roads[side]; if (!rd) continue;
      if (inPoly([rd.n1, rd.n2, rd.F1, rd.P, rd.F0])) return side;
    }
    return null;
  };
  // Map an editable side field to its road side (when that side has a road).
  const roadSideForField = (f: string): string | null => (
    f === 'north_txt' ? 'top' : f === 'south_txt' ? 'bottom' : f === 'east_txt' ? 'right' : f === 'west_txt' ? 'left' : null
  );
  // Ensure roadGeom[side] exists, seeded from the current geometry so partial
  // edits don't reset the road.
  const ensureRoad = (side: string) => {
    const win: any = getIframe()?.contentWindow; const rd = snap?.roads[side];
    const RG = win.roadGeom || (win.roadGeom = {});
    const r = RG[side] || (RG[side] = {});
    if (rd && r.width == null) r.width = rd.width;
    return r;
  };
  // Drag a road handle: width (far line out/in), end0/end1 (free — extend/rotate
  // each far segment to make an L), or pivot (move the off-centre corner).
  const startRoadDrag = (e: React.PointerEvent, side: string, kind: 'width' | 'end0' | 'end1' | 'pivot') => {
    e.preventDefault(); e.stopPropagation();
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    const rd = snap.roads[side]; if (!rd) return;
    const cf = ((win.roadGeom && win.roadGeom[side] && win.roadGeom[side].far) || {}) as any;
    const base = {
      width: rd.width, pdx: cf.pdx || 0, pdy: cf.pdy || 0,
      e0: { dx: (cf.e0 && cf.e0.dx) || 0, dy: (cf.e0 && cf.e0.dy) || 0 },
      e1: { dx: (cf.e1 && cf.e1.dx) || 0, dy: (cf.e1 && cf.e1.dy) || 0 },
    };
    const sx = e.clientX, sy = e.clientY; let raf = 0, ndx = 0, ndy = 0;
    const flush = () => {
      raf = 0;
      const svgDx = ndx / k, svgDy = ndy / k;
      const pN = svgDx * rd.outNorm.x + svgDy * rd.outNorm.y;
      const r = ensureRoad(side);
      const f = r.far || (r.far = { pdx: base.pdx, pdy: base.pdy, e0: { ...base.e0 }, e1: { ...base.e1 } });
      if (kind === 'width') r.width = Math.max(8, base.width + pN);
      else if (kind === 'end0') f.e0 = { dx: base.e0.dx + svgDx, dy: base.e0.dy + svgDy };
      else if (kind === 'end1') f.e1 = { dx: base.e1.dx + svgDx, dy: base.e1.dy + svgDy };
      else if (kind === 'pivot') { f.pdx = base.pdx + svgDx; f.pdy = base.pdy + svgDy; }
      try { win.drawMap(); } catch (err) { /* ignore */ }
      snapshot();
    };
    const move = (ev: PointerEvent) => { ndx = ev.clientX - sx; ndy = ev.clientY - sy; if (!raf) raf = requestAnimationFrame(flush); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) { cancelAnimationFrame(raf); flush(); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const removeBend = (side: string) => {
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    ensureRoad(side).far = { pdx: 0, pdy: 0, e0: { dx: 0, dy: 0 }, e1: { dx: 0, dy: 0 } };
    try { win.drawMap(); } catch (e) { /* ignore */ }
    snapshot();
  };
  // Drag a pulse to move it (default) or scale it (scale=true).
  const startPulseDrag = (e: React.PointerEvent, key: string, scale: boolean) => {
    e.preventDefault(); e.stopPropagation();
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    const [pside, pidx] = key.split('|'); const pk = 'pulse' + pidx;
    const r = ensureRoad(pside); const cur = r[pk] || {};
    const base = { dx: cur.dx || 0, dy: cur.dy || 0, s: cur.s || 1 };
    const sx = e.clientX, sy = e.clientY; let raf = 0, ndx = 0, ndy = 0;
    const flush = () => {
      raf = 0;
      const svgDx = ndx / k, svgDy = ndy / k;
      const rr = ensureRoad(pside);
      const keep = rr[pk] || {};
      if (scale) rr[pk] = { ...keep, dx: base.dx, dy: base.dy, s: Math.max(0.3, base.s + (svgDx + svgDy) / 60) };
      else rr[pk] = { ...keep, dx: base.dx + svgDx, dy: base.dy + svgDy, s: base.s };
      try { win.drawMap(); } catch (err) { /* ignore */ }
      snapshot();
    };
    const move = (ev: PointerEvent) => { ndx = ev.clientX - sx; ndy = ev.clientY - sy; if (!raf) raf = requestAnimationFrame(flush); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) { cancelAnimationFrame(raf); flush(); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Rotate a pulse around its centre.
  const startPulseRotate = (e: React.PointerEvent, key: string) => {
    e.preventDefault(); e.stopPropagation();
    const win: any = getIframe()?.contentWindow; if (!win || !snap) return;
    const [pside, pidx] = key.split('|'); const pk = 'pulse' + pidx;
    const rd = snap.roads[pside]; const pu = rd && rd.pulses && rd.pulses.find((p: any) => p.key === key); if (!pu) return;
    const cont = figRef.current; if (!cont) return;
    const rect = cont.getBoundingClientRect();
    const cScreen = { x: rect.left + (pu.cx - (snap.vbX || 0)) * k, y: rect.top + (pu.cy - (snap.vbY || 0)) * k };
    const base = (ensureRoad(pside)[pk] || {}).r || 0;
    const startAng = Math.atan2(e.clientY - cScreen.y, e.clientX - cScreen.x);
    let raf = 0, ang = startAng;
    const flush = () => { raf = 0; const r = ensureRoad(pside); r[pk] = { ...(r[pk] || {}), r: base + (ang - startAng) * 180 / Math.PI }; try { win.drawMap(); } catch (e2) { /* ignore */ } snapshot(); };
    const move = (ev: PointerEvent) => { ang = Math.atan2(ev.clientY - cScreen.y, ev.clientX - cScreen.x); if (!raf) raf = requestAnimationFrame(flush); };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); if (raf) { cancelAnimationFrame(raf); flush(); } };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const setPulseFlag = (key: string, on: boolean) => {
    const win: any = getIframe()?.contentWindow; if (!win) return;
    const [pside, pidx] = key.split('|'); const r = ensureRoad(pside); const pk = 'pulse' + pidx;
    r[pk] = { ...(r[pk] || {}), on };
    try { win.drawMap(); } catch (e) { /* ignore */ }
    setSelPulse(on ? key : null);
    snapshot();
  };
  const onFigDoubleClick = (e: React.MouseEvent) => {
    const el = fieldElFrom(e.target as Element) || pickField(e.clientX, e.clientY);
    if (!el) return;
    const field = el.getAttribute('data-field') || '';
    // Dimensions are driven by the panel on the right — don't inline-edit them.
    if (/^dim_txt_/.test(field)) { setEditing(null); flashNote('Edit dimensions in the panel on the right →'); return; }
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument; const cont = figRef.current; if (!doc || !cont) return;
    const inputEl = doc.getElementById(field) as HTMLInputElement | HTMLTextAreaElement | null;
    const cur = inputEl ? inputEl.value : (el.textContent || '');
    const ov = (win?.textOverrides && win.textOverrides[field]) || {};
    const r = el.getBoundingClientRect(); const cr = cont.getBoundingClientRect();
    setSel(null); setEditPos(null);
    setEditing({ field, left: r.left - cr.left, top: r.top - cr.top, eh: r.height, value: cur, b: !!ov.b, i: !!ov.i, u: !!ov.u, align: ov.align || '', case: ov.case || '', sc: !!ov.sc, ds: ov.ds || 0, lh: ov.lh || 1.2, fs: ov.fs || 0 });
  };
  // Apply the textarea content to the figure live as the user types.
  const liveEditApply = (val: string) => {
    if (!editing) return;
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument;
    const inputEl = doc?.getElementById(editing.field) as HTMLInputElement | HTMLTextAreaElement | null;
    if (inputEl && win) { inputEl.value = val; try { win.drawMap(); } catch (e) { /* ignore */ } requestAnimationFrame(snapshot); }
  };
  // Apply a styling patch to the field, live, and reflect it in the toolbar.
  const setFieldStyle = (patch: Record<string, any>) => {
    const win: any = getIframe()?.contentWindow; if (!win || !editing) return;
    const T = win.textOverrides || (win.textOverrides = {});
    if (!T[editing.field]) T[editing.field] = { dx: 0, dy: 0, ds: 0, dw: 0 };
    Object.assign(T[editing.field], patch);
    try { win.drawMap(); } catch (e) { /* ignore */ }
    setEditing(ed => ed ? { ...ed, ...patch } as any : ed);
    requestAnimationFrame(snapshot);
  };
  const commitEdit = (val: string) => {
    if (!editing) return;
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument;
    const inputEl = doc?.getElementById(editing.field) as HTMLInputElement | HTMLTextAreaElement | null;
    if (inputEl && win) {
      inputEl.value = val;
      // Persist (debounced save) AND redraw immediately so the next snapshot
      // captures the new text, not the previous frame.
      try { inputEl.dispatchEvent(new win.Event('input', { bubbles: true })); } catch (e) { /* ignore */ }
      try { win.drawMap(); } catch (e) { /* ignore */ }
    }
    setEditing(null);
    requestAnimationFrame(snapshot);
  };

  const applyDimsDirect = (d: { n: number; s: number; e: number; w: number }, anchor = 'top-left') => {
    const ifr = getIframe(); const win = ifr?.contentWindow;
    win?.postMessage({ type: 'SET_BOUNDARIES', shape: shapeRef.current, n: r1(d.n), s: r1(d.s), e: r1(d.e), w: r1(d.w), anchor }, '*');
    setTimeout(snapshot, 80);
  };
  // Set engine form fields (checkboxes) and redraw the figure.
  const setFields = (fields: Record<string, boolean>) => {
    const ifr = getIframe(); const win = ifr?.contentWindow;
    win?.postMessage({ type: 'SET_FIELDS', fields }, '*');
    setTimeout(snapshot, 80);
  };
  // Manual area override: set the input value + the engine's manual flag so it
  // isn't auto-recomputed (blank reverts to auto).
  const setArea = (id: string, val: string) => {
    const win: any = getIframe()?.contentWindow; const doc = getIframe()?.contentDocument;
    const el = doc?.getElementById(id) as HTMLInputElement | null;
    if (el) { el.value = val; el.dataset.manualOverride = val.trim() === '' ? '0' : '1'; }
    try { win.drawMap(); } catch (e) { /* ignore */ }
    setTimeout(snapshot, 60);
  };
  const toggleAreaOverride = () => {
    const doc = getIframe()?.contentDocument;
    const next = !areaOverride;
    setAreaOverride(next);
    if (next) {
      // Seed override fields with the current calculated values, then lock them.
      const yd = (doc?.getElementById('area_sqyd_override') as HTMLInputElement | null)?.value || '';
      const mt = (doc?.getElementById('area_sqm_override') as HTMLInputElement | null)?.value || '';
      setAreaYd(yd); setAreaMt(mt);
      setArea('area_sqyd_override', yd); setArea('area_sqm_override', mt);
    } else {
      setAreaYd(''); setAreaMt('');
      setArea('area_sqyd_override', ''); setArea('area_sqm_override', '');
    }
  };
  const toggleName = () => { const v = !showName; setShowName(v); setFields({ show_plot_name: v }); };
  const toggleDimsOutside = (v: boolean) => { setDimsOutside(v); setFields({ dims_outside: v }); };
  const toggleRoad = (side: 'north' | 'south' | 'east' | 'west') => { const v = !roads[side]; setRoads(r => ({ ...r, [side]: v })); setFields({ ['road_' + side]: v }); };
  const commitSide = (key: 'n' | 's' | 'e' | 'w', val: number) => {
    const v = Math.max(1, val || 0);
    const d = { ...dims };
    if (shape === 'rectangle') { if (key === 'n' || key === 's') { d.n = v; d.s = v; } else { d.e = v; d.w = v; } }
    else d[key] = v;
    setDims(d);
    applyDimsDirect(d, 'top-left');
  };

  const doConvertShape = (sh: Shape) => { setShape(sh); shapeRef.current = sh; setConfirmRect(false); applyDimsDirect(dims, 'center'); };
  const toggleShape = (sh: Shape) => {
    if (sh === shape) return;
    // Trapezoid -> rectangle flattens the shape: confirm first (don't lose work).
    if (shape === 'trapezoid' && sh === 'rectangle') { setConfirmRect(true); return; }
    doConvertShape(sh);
  };
  const cancel = () => {
    const o = origRef.current; const ifr = getIframe(); const win = ifr?.contentWindow;
    if (o && win) win.postMessage({ type: 'SET_BOUNDARIES', shape: o.shape, n: r1(o.n), s: r1(o.s), e: r1(o.e), w: r1(o.w), anchor: 'center' }, '*');
    onClose();
  };

  const ptsPx = pts.map(toPx);
  const path = ptsPx.length ? ptsPx.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ') + ' Z' : '';
  const seg = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6, background: active ? '#FFD93D' : '#1a1a1a',
    color: active ? '#1a1a1a' : '#bbb', border: '1px solid ' + (active ? '#FFD93D' : '#333'),
    borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  });

  const zoomBtn: React.CSSProperties = { width: 24, height: 24, borderRadius: 6, border: '1px solid #3a3a3a', background: '#1f1f1f', color: '#ddd', cursor: 'pointer', fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  const numInput = (label: string, key: 'n' | 's' | 'e' | 'w', value: number) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
      <span style={{ color: '#aaa', fontSize: 11 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="number" min={1} value={value}
          onChange={ev => setDims(d => ({ ...d, [key]: parseFloat(ev.target.value) || 0, ...(shape === 'rectangle' ? (key === 'n' ? { s: parseFloat(ev.target.value) || 0 } : { w: parseFloat(ev.target.value) || 0 }) : {}) }))}
          onBlur={ev => commitSide(key, parseFloat(ev.target.value) || 0)}
          onKeyDown={ev => { if (ev.key === 'Enter') (ev.target as HTMLInputElement).blur(); }}
          style={{ width: '100%', background: '#1a1a1a', border: '1px solid #333', color: '#fff', borderRadius: 6, padding: '8px 9px', fontSize: 13 }} />
        <span style={{ color: '#777', fontSize: 11 }}>ft</span>
      </div>
    </label>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 410, background: 'rgba(0,0,0,0.7)', display: 'flex' }}>
      {/* Confirm before a preset replaces an existing drawing. */}
      {pendingPreset && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 520, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#17181a', border: '1px solid #333', borderRadius: 14, padding: 20, maxWidth: 340, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,.6)' }}>
            <div style={{ color: '#eee', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Replace current drawing?</div>
            <div style={{ color: '#9aa0a6', fontSize: 13, marginBottom: 18, lineHeight: 1.45 }}>You already have a house layout. Replace it with this preset, or add the preset to it?</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setPendingPreset(null)} style={{ background: 'transparent', color: '#aaa', border: '1px solid #3a3a3a', borderRadius: 7, padding: '7px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => pendingPreset && applyPreset(pendingPreset, 'add')} style={{ background: '#2a2a2a', color: '#ddd', border: '1px solid #444', borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Add</button>
              <button onClick={() => pendingPreset && applyPreset(pendingPreset, 'replace')} style={{ background: '#FFD93D', color: '#1a1a1a', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Replace</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ margin: isMobile ? 0 : 'auto',
        width: isMobile ? '100vw' : (fullscreen ? '98vw' : 1060), maxWidth: isMobile ? 'none' : (fullscreen ? 'none' : '97vw'),
        height: isMobile ? '100dvh' : (fullscreen ? '96vh' : 820), maxHeight: isMobile ? 'none' : (fullscreen ? 'none' : '95vh'),
        display: 'flex', flexDirection: 'column', background: '#161616', borderRadius: isMobile ? 0 : (fullscreen ? 8 : 14), border: isMobile ? 'none' : '1px solid #2a2a2a', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #222', flexShrink: 0, gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ color: '#FFD93D', fontSize: 16, fontWeight: 800 }}>House</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={undo} title="Undo (Ctrl/Cmd+Z)" style={{ display: 'flex', alignItems: 'center', background: 'none', border: '1px solid #333', borderRadius: 6, color: '#bbb', cursor: 'pointer', padding: '5px 7px' }}><Undo2 size={14} /></button>
              <button onClick={redo} title="Redo (Ctrl+Shift+Z / Ctrl+Y)" style={{ display: 'flex', alignItems: 'center', background: 'none', border: '1px solid #333', borderRadius: 6, color: '#bbb', cursor: 'pointer', padding: '5px 7px' }}><Redo2 size={14} /></button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {!isMobile && (
              <button onClick={() => setFullscreen((v) => !v)} title={fullscreen ? 'Exit full width' : 'Full width'}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid #333', borderRadius: 6, color: '#bbb', cursor: 'pointer', padding: '5px 9px', fontSize: 12 }}>
                {fullscreen ? <><Minimize2 size={13} /> Exit</> : <><Maximize2 size={13} /> Full width</>}
              </button>
            )}
            <button onClick={cancel} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
          </div>
        </div>

        {/* Two columns on desktop; stacked (canvas over panel) on mobile. */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 0, minHeight: 0, flex: 1 }}>
          {/* LEFT: actual figure canvas */}
          <div style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
          {/* Mobile canvas lock toggle — freezes panning so element drags don't move the canvas. */}
          {isMobile && (
            <button onClick={() => setCanvasLocked((v) => !v)}
              title={canvasLocked ? 'Canvas locked — tap to allow panning' : 'Canvas movable — tap to lock for editing'}
              style={{ position: 'absolute', top: 8, left: 8, zIndex: 7, display: 'flex', alignItems: 'center', gap: 5,
                background: canvasLocked ? '#FFD93D' : 'rgba(13,13,13,.9)', color: canvasLocked ? '#1a1a1a' : '#ddd',
                border: '1px solid ' + (canvasLocked ? '#FFD93D' : '#3a3a3a'), borderRadius: 8, padding: '6px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {canvasLocked ? <><Lock size={13} /> Editing</> : <><Move size={13} /> Pan</>}
            </button>
          )}
          <div ref={scrollRef} style={{ position: 'relative', padding: 18, overflow: (isMobile && canvasLocked) ? 'hidden' : 'auto', touchAction: (isMobile && canvasLocked) ? 'none' : 'auto', display: 'flex', alignItems: 'safe center', justifyContent: 'safe center', background: '#0e0e0e', flex: 1, minWidth: 0 }}>
            <div style={{ position: 'relative', width: dW, height: dH, flexShrink: 0 }}>
              {/* Faded, non-editable plot reference. House elements get placed on top. */}
              <div ref={figRef}
                onPointerDown={() => { if (!drawMode) { setSelRoom(null); setToolbarRoom(null); setSelIds([]); } }}
                style={{ position: 'absolute', inset: 0, borderRadius: 6, overflow: 'hidden', boxShadow: '0 2px 18px rgba(0,0,0,0.5)', background: '#fff' }} />
              {/* Light reference grid (over the figure, below the handles). */}
              {snap && (
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: 6,
                  backgroundImage: 'linear-gradient(rgba(45,156,219,.18) 1px, transparent 1px), linear-gradient(90deg, rgba(45,156,219,.18) 1px, transparent 1px), linear-gradient(rgba(45,156,219,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(45,156,219,.08) 1px, transparent 1px)',
                  backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
                }} />
              )}
              {/* Free-draw capture layer (only when drawing) */}
              {snap && drawMode && (
                <div onPointerDown={onCanvasPointerDown} onDoubleClick={() => { if (drawTool === 'line') finishPoly(); }} style={{ position: 'absolute', inset: 0, cursor: 'crosshair', zIndex: 4 }} />
              )}
              {/* Interaction overlay. The rooms themselves are drawn by the ENGINE
                  (exact match with the document); here we add invisible hit-areas
                  for select/move plus a selection outline + corner resize handles. */}
              {snap && (
                <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                  {rooms.map((room) => {
                    const pts = (room.points || []).map((p) => normToPx(p.x, p.y));
                    if (pts.length < 2) return null;
                    // Dimension arrow: line hit-area for move + an endpoint handle each end.
                    if (room.type === 'dim') {
                      const A = pts[0], B = pts[pts.length - 1];
                      const selD = selRoom === room.id || selIds.includes(room.id);
                      return (
                        <g key={room.id}>
                          <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="transparent" strokeWidth={16}
                            style={{ cursor: 'move', pointerEvents: 'all' }} onPointerDown={(e) => onElPointerDown(e, room.id)}
                            onDoubleClick={(e) => { e.stopPropagation(); setSelRoom(room.id); setToolbarRoom(room.id); }} />
                          {selD && <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="#2f7bff" strokeWidth={1.4} strokeDasharray="5,4" style={{ pointerEvents: 'none' }} />}
                          {selD && [A, B].map((p, i) => {
                            const o = i === 0 ? B : A;
                            const vx = p.x - o.x, vy = p.y - o.y; const L = Math.hypot(vx, vy) || 1;
                            const hx = p.x + (vx / L) * 13, hy = p.y + (vy / L) * 13; // grip just outside the tip
                            return (
                              <g key={i}>
                                <line x1={p.x} y1={p.y} x2={hx} y2={hy} stroke="#2f7bff" strokeWidth={1} style={{ pointerEvents: 'none' }} />
                                <circle cx={hx} cy={hy} r={7} fill="rgba(47,123,255,0.18)" stroke="#2f7bff" strokeWidth={2.5}
                                  style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startDimEndDrag(e, room.id, i)} />
                              </g>
                            );
                          })}
                        </g>
                      );
                    }
                    const ptsStr = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
                    const sel = selRoom === room.id || selIds.includes(room.id);
                    // bbox in px; the selection box/handles sit a margin OUTSIDE the
                    // room so the border + overlap with neighbours stays visible.
                    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
                    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
                    const PAD = 7;
                    const ox0 = x0 - PAD, oy0 = y0 - PAD, ox1 = x1 + PAD, oy1 = y1 + PAD;
                    const corners = [{ x: ox0, y: oy0 }, { x: ox1, y: oy0 }, { x: ox1, y: oy1 }, { x: ox0, y: oy1 }];
                    return (
                      <g key={room.id}>
                        {/* transparent hit-area: click = select/move (Shift/⌘/Ctrl = multi-select), double click = toolbar */}
                        <polygon points={ptsStr} fill="transparent"
                          style={{ cursor: 'move', pointerEvents: 'all' }} onPointerDown={(e) => onElPointerDown(e, room.id)}
                          onDoubleClick={(e) => { e.stopPropagation(); setSelRoom(room.id); setToolbarRoom(room.id); }} />
                        {sel && (
                          <rect x={ox0} y={oy0} width={ox1 - ox0} height={oy1 - oy0}
                            fill="none" stroke="#2f7bff" strokeWidth={1.6} strokeDasharray="6,4" style={{ pointerEvents: 'none' }} />
                        )}
                        {sel && corners.map((c, i) => (
                          <rect key={i} x={c.x - 6} y={c.y - 6} width={12} height={12}
                            fill="#fff" stroke="#2f7bff" strokeWidth={2}
                            style={{ cursor: i === 0 || i === 2 ? 'nwse-resize' : 'nesw-resize', pointerEvents: 'all' }}
                            onPointerDown={(e) => startRoomResize(e, room.id, i)} />
                        ))}
                        {/* Door handles (drag to slide/move to any wall; × to delete) */}
                        {sel && (room.doors || []).map((d) => {
                          const c = normToPx(doorCenterNorm(room, d).x, doorCenterNorm(room, d).y);
                          return (
                            <g key={d.id}>
                              <circle cx={c.x} cy={c.y} r={7} fill="#FFD93D" stroke="#1a1a1a" strokeWidth={1.5}
                                style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startDoorDrag(e, room.id, d.id)} />
                              <g style={{ cursor: 'pointer', pointerEvents: 'all' }} onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); deleteDoor(room.id, d.id); }}>
                                <circle cx={c.x + 12} cy={c.y - 12} r={6.5} fill="#e53935" stroke="#fff" strokeWidth={1} />
                                <text x={c.x + 12} y={c.y - 12} textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700} fill="#fff" style={{ pointerEvents: 'none' }}>×</text>
                              </g>
                            </g>
                          );
                        })}
                      </g>
                    );
                  })}
                  {drawRect && (
                    <polygon points={rectPts(drawRect.x0, drawRect.y0, drawRect.x1, drawRect.y1).map((p) => { const q = normToPx(p.x, p.y); return `${q.x},${q.y}`; }).join(' ')}
                      fill="rgba(255,217,61,0.18)" stroke="#FFD93D" strokeWidth={2} strokeDasharray="5,4" />
                  )}
                  {/* Line/polygon in progress: segments + vertices (first dot bigger = click to close). */}
                  {drawMode && drawTool === 'line' && polyPts.length > 0 && (() => {
                    const px = polyPts.map((p) => normToPx(p.x, p.y));
                    return (
                      <g style={{ pointerEvents: 'none' }}>
                        {px.length > 1 && <polyline points={px.map((q) => `${q.x},${q.y}`).join(' ')} fill="none" stroke="#FFD93D" strokeWidth={2} />}
                        {px.length >= 3 && <line x1={px[px.length - 1].x} y1={px[px.length - 1].y} x2={px[0].x} y2={px[0].y} stroke="#FFD93D" strokeWidth={1.4} strokeDasharray="4,3" opacity={0.6} />}
                        {px.map((q, i) => (
                          <circle key={i} cx={q.x} cy={q.y} r={i === 0 ? 6 : 4} fill={i === 0 ? '#FFD93D' : '#fff'} stroke="#FFD93D" strokeWidth={2} />
                        ))}
                      </g>
                    );
                  })()}
                  {/* Whole-house scale box: dashed bounds + 4 corner handles. */}
                  {scaleMode && !drawMode && rooms.length > 0 && (() => {
                    const b = houseBounds(); if (!b) return null;
                    const p0 = normToPx(b.x0, b.y0), p1 = normToPx(b.x1, b.y1);
                    const gx0 = Math.min(p0.x, p1.x), gy0 = Math.min(p0.y, p1.y), gx1 = Math.max(p0.x, p1.x), gy1 = Math.max(p0.y, p1.y);
                    const hc = [{ x: gx0, y: gy0 }, { x: gx1, y: gy0 }, { x: gx1, y: gy1 }, { x: gx0, y: gy1 }];
                    return (
                      <g>
                        {/* interior hit-area: drag to MOVE the whole house */}
                        <rect x={gx0} y={gy0} width={gx1 - gx0} height={gy1 - gy0} fill="transparent"
                          style={{ cursor: 'move', pointerEvents: 'all' }} onPointerDown={startGroupMove} />
                        <rect x={gx0 - 4} y={gy0 - 4} width={gx1 - gx0 + 8} height={gy1 - gy0 + 8}
                          fill="rgba(46,204,113,0.06)" stroke="#2ecc71" strokeWidth={1.6} strokeDasharray="7,4" style={{ pointerEvents: 'none' }} />
                        {hc.map((c, i) => {
                          const ox = (i === 0 || i === 3) ? -6 : 6; // nudge handle outward from the box
                          const oy = (i === 0 || i === 1) ? -6 : 6;
                          return (
                            <rect key={i} x={c.x + ox - 7} y={c.y + oy - 7} width={14} height={14}
                              rx={3} fill="#fff" stroke="#2ecc71" strokeWidth={2.5}
                              style={{ cursor: (i === 0 || i === 2) ? 'nwse-resize' : 'nesw-resize', pointerEvents: 'all' }}
                              onPointerDown={(e) => startGroupScale(e, i)} />
                          );
                        })}
                      </g>
                    );
                  })()}
                  {/* Multi-selection (2+ shift-clicked): move interior / resize corners together. */}
                  {!scaleMode && !drawMode && selIds.length > 1 && (() => {
                    const b = houseBounds(selIds); if (!b) return null;
                    const p0 = normToPx(b.x0, b.y0), p1 = normToPx(b.x1, b.y1);
                    const gx0 = Math.min(p0.x, p1.x), gy0 = Math.min(p0.y, p1.y), gx1 = Math.max(p0.x, p1.x), gy1 = Math.max(p0.y, p1.y);
                    const hc = [{ x: gx0, y: gy0 }, { x: gx1, y: gy0 }, { x: gx1, y: gy1 }, { x: gx0, y: gy1 }];
                    return (
                      <g>
                        <rect x={gx0} y={gy0} width={gx1 - gx0} height={gy1 - gy0} fill="transparent"
                          style={{ cursor: 'move', pointerEvents: 'all' }}
                          onPointerDown={(e) => {
                            if (isMultiKey(e)) { e.preventDefault(); e.stopPropagation(); const n = pxToNorm(e.clientX, e.clientY); const hit = elAt(n.x, n.y); if (hit) toggleSel(hit); return; }
                            startGroupMove(e, selIds);
                          }} />
                        <rect x={gx0 - 4} y={gy0 - 4} width={gx1 - gx0 + 8} height={gy1 - gy0 + 8}
                          fill="rgba(47,123,255,0.06)" stroke="#2f7bff" strokeWidth={1.6} strokeDasharray="6,4" style={{ pointerEvents: 'none' }} />
                        {hc.map((c, i) => {
                          const ox = (i === 0 || i === 3) ? -6 : 6;
                          const oy = (i === 0 || i === 1) ? -6 : 6;
                          return (
                            <rect key={i} x={c.x + ox - 7} y={c.y + oy - 7} width={14} height={14} rx={3}
                              fill="#fff" stroke="#2f7bff" strokeWidth={2.5}
                              style={{ cursor: (i === 0 || i === 2) ? 'nwse-resize' : 'nesw-resize', pointerEvents: 'all' }}
                              onPointerDown={(e) => startGroupScale(e, i, selIds)} />
                          );
                        })}
                      </g>
                    );
                  })()}
                </svg>
              )}
              {/* Selection action bar: count + delete (single or multi; hidden while the per-room toolbar is open) */}
              {snap && !drawMode && !scaleMode && !toolbarRoom && selIds.length > 0 && (() => {
                const b = houseBounds(selIds); if (!b) return null;
                const p0 = normToPx(b.x0, b.y0), p1 = normToPx(b.x1, b.y1);
                const cx = (p0.x + p1.x) / 2, top = Math.min(p0.y, p1.y);
                return (
                  <div style={{ position: 'absolute', left: cx, top: Math.max(2, top - 40), transform: 'translateX(-50%)', zIndex: 6, display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(13,13,13,.96)', border: '1px solid #333', borderRadius: 8, padding: '4px 8px', boxShadow: '0 4px 14px rgba(0,0,0,.5)', whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#aaa', fontSize: 11, fontWeight: 600 }}>{selIds.length} selected</span>
                    {selIds.length > 1 && !selHasGroup() && (
                      <button onClick={mergeSelected} title="Merge into one shape (L/T/Z room, single label)"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#FFD93D', color: '#1a1a1a', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        <Combine size={13} /> Merge
                      </button>
                    )}
                    {selHasGroup() && (
                      <button onClick={unmergeSelected} title="Split the merged shape back into rooms"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', color: '#ddd', border: '1px solid #444', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        <Ungroup size={13} /> Unmerge
                      </button>
                    )}
                    <button onClick={deleteSelected} title="Delete selection (Del)"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', color: '#e57373', border: '1px solid #5a2a2a', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                );
              })()}
              {/* Floating per-room toolbar (double-click a room to open) */}
              {snap && toolbarRoom && !drawMode && (() => {
                const room = rooms.find((r) => r.id === toolbarRoom); if (!room) return null;
                const pts = room.points.map((p) => normToPx(p.x, p.y));
                const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
                const tx = (Math.min(...xs) + Math.max(...xs)) / 2; const ty = Math.min(...ys);
                const tbtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, background: '#FFD93D', color: '#1a1a1a', border: 'none', borderRadius: 6, padding: '5px 9px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
                const stepBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 26, background: '#1f1f1f', color: '#ddd', border: '1px solid #3a3a3a', borderRadius: 5, cursor: 'pointer', fontWeight: 700, lineHeight: 1 };
                const curScale = room.labelScale || 1;
                // Nudge this room's label size (per-room), clamped to a sensible range.
                const bumpScale = (d: number) => patchRoom(room.id, (r) => ({ ...r, labelScale: Math.min(3, Math.max(0.4, Math.round(((r.labelScale || 1) + d) * 100) / 100)) }));
                return (
                  <div style={{ position: 'absolute', left: tx, top: Math.max(2, ty - 44), transform: 'translateX(-50%)', zIndex: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 6, maxWidth: 360, background: 'rgba(13,13,13,.96)', border: '1px solid #333', borderRadius: 9, padding: '5px 6px', boxShadow: '0 4px 14px rgba(0,0,0,.5)' }}>
                    {/* Edit the room's label text — multi-line (e.g. "Bedroom" / "10x10"). Enter adds a new line. */}
                    <textarea value={room.label} placeholder={room.type === 'dim' ? 'Auto value' : 'Room name\n10x10'} rows={2}
                      onPointerDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onChange={(e) => setRoomLabel(room.id, e.target.value)}
                      style={{ width: 118, background: '#161616', color: '#fff', border: '1px solid #3a3a3a', borderRadius: 5, padding: '5px 7px', fontSize: 12, outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.25 }} />
                    {/* Flexible label text size (per room) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <button title="Smaller text" onClick={() => bumpScale(-0.15)} style={{ ...stepBtn, fontSize: 10 }}>A−</button>
                      <span style={{ color: '#aaa', fontSize: 11, minWidth: 34, textAlign: 'center' }}>{Math.round(curScale * 100)}%</span>
                      <button title="Larger text" onClick={() => bumpScale(0.15)} style={{ ...stepBtn, fontSize: 14 }}>A+</button>
                    </div>
                    {/* Trapezium variations: narrow side (4 orientations) + slant slider. */}
                    {room.type === 'trapezium' && (() => {
                      const dir = room.trapDir || 'top';
                      const inset = room.trapInset ?? 0.22;
                      const glyph = (dd: string) => {
                        const pts = dd === 'top' ? '4,3 10,3 12,11 2,11' : dd === 'bottom' ? '2,3 12,3 10,11 4,11' : dd === 'left' ? '3,4 11,2 11,12 3,10' : '3,2 11,4 11,10 3,12';
                        return <svg width={12} height={12} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.3} strokeLinejoin="round"><polygon points={pts} /></svg>;
                      };
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <div style={{ display: 'flex', gap: 2 }}>
                            {(['top', 'bottom', 'left', 'right'] as const).map((dd) => (
                              <button key={dd} title={`Narrow ${dd}`} onClick={() => applyTrapVariation(room.id, dd, inset)}
                                style={{ ...stepBtn, width: 24, background: dir === dd ? '#FFD93D' : '#1f1f1f', color: dir === dd ? '#1a1a1a' : '#ddd', border: '1px solid ' + (dir === dd ? '#FFD93D' : '#3a3a3a') }}>{glyph(dd)}</button>
                            ))}
                          </div>
                          <input type="range" min={5} max={45} value={Math.round(inset * 100)} title="Slant"
                            onPointerDown={(e) => e.stopPropagation()}
                            onChange={(e) => applyTrapVariation(room.id, dir, parseInt(e.target.value, 10) / 100)}
                            style={{ width: 64, accentColor: '#FFD93D', cursor: 'pointer' }} />
                        </div>
                      );
                    })()}
                    {room.type !== 'dim' && room.type !== 'trapezium' && <button onClick={() => addDoor(room.id)} style={tbtn}><DoorOpen size={13} /> Door</button>}
                    {room.type === 'trapezium' && <button onClick={() => addDoor(room.id)} title="Add door" style={{ ...stepBtn, width: 28 }}><DoorOpen size={13} /></button>}
                    <button onClick={() => deleteRoom(room.id)} title={room.type === 'dim' ? 'Delete dimension' : 'Delete room'} style={{ display: 'flex', alignItems: 'center', background: 'transparent', color: '#e57373', border: '1px solid #5a2a2a', borderRadius: 6, padding: '5px 7px', cursor: 'pointer' }}><Trash2 size={13} /></button>
                  </div>
                );
              })()}
              {!snap && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Loader2 size={28} className="animate-spin" style={{ color: '#FFD93D' }} />
                </div>
              )}
              {/* Selected-element outline (text / label / dimension) */}
              {sel && (
                <div style={{ position: 'absolute', left: sel.left - 3, top: sel.top - 3, width: sel.width + 6, height: sel.height + 6, border: '1.5px solid #2f7bff', borderRadius: 3, pointerEvents: 'none' }} />
              )}
              {/* Boundary path + reshape corners — only when the plot is selected */}
              {snap && selFig && (
                <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, touchAction: 'none', pointerEvents: 'none' }}>
                  <path d={path} fill="rgba(76,144,255,0.10)" stroke="#2f7bff" strokeWidth={2} strokeDasharray="6,4" strokeLinejoin="round" />
                  {ptsPx.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={6} fill="transparent" stroke="#2f7bff" strokeWidth={2.5}
                      style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startDrag(e, i)} />
                  ))}
                </svg>
              )}
              {/* Road handles for the SELECTED road — width (orange), end dots
                  (blue), pivot (yellow), pulses (purple shown / green + to add). */}
              {snap && selRoad && snap.roads && snap.roads[selRoad] && (() => {
                const rd = snap.roads[selRoad]; if (!rd) return null;
                const widH = toPx({ x: (rd.fb1.x + rd.fb2.x) / 2, y: (rd.fb1.y + rd.fb2.y) / 2 });
                const f0 = toPx(rd.F0), f1 = toPx(rd.F1), pv = toPx(rd.P);
                const H = (x: number, y: number, color: string, on: any, dbl?: any, r = 5) => (
                  <circle cx={x} cy={y} r={r} fill="transparent" stroke={color} strokeWidth={2.5}
                    style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={on} onDoubleClick={dbl} />
                );
                return (
                  <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                    {H(f0.x, f0.y, '#2f7bff', (e: any) => startRoadDrag(e, selRoad, 'end0'))}
                    {H(f1.x, f1.y, '#2f7bff', (e: any) => startRoadDrag(e, selRoad, 'end1'))}
                    {H(widH.x, widH.y, '#e67e22', (e: any) => startRoadDrag(e, selRoad, 'width'))}
                    {H(pv.x, pv.y, '#FFD93D', (e: any) => startRoadDrag(e, selRoad, 'pivot'), () => removeBend(selRoad))}
                    {(rd.pulses || []).map((pu: any) => {
                      const c = toPx({ x: pu.cx, y: pu.cy });
                      if (pu.shown) return (
                        <circle key={pu.key} cx={c.x} cy={c.y} r={6} fill="transparent" stroke="#9b59b6" strokeWidth={2.5}
                          style={{ cursor: 'grab', pointerEvents: 'all' }}
                          onPointerDown={(e) => { e.stopPropagation(); setSelPulse(pu.key); startPulseDrag(e, pu.key, false); }}
                          onDoubleClick={() => setPulseFlag(pu.key, false)} />
                      );
                      return (
                        <g key={pu.key} style={{ cursor: 'pointer', pointerEvents: 'all' }}
                          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setPulseFlag(pu.key, true); }}>
                          <circle cx={c.x} cy={c.y} r={7} fill="rgba(46,204,113,.15)" stroke="#2ecc71" strokeWidth={2} strokeDasharray="3,2" />
                          <line x1={c.x - 3} y1={c.y} x2={c.x + 3} y2={c.y} stroke="#2ecc71" strokeWidth={1.5} />
                          <line x1={c.x} y1={c.y - 3} x2={c.x} y2={c.y + 3} stroke="#2ecc71" strokeWidth={1.5} />
                        </g>
                      );
                    })}
                  </svg>
                );
              })()}
              {/* Selected pulse — small dot to move, ring to scale/extend, dbl-click to delete. */}
              {snap && selPulse && (() => {
                const [pside] = selPulse.split('|');
                const rd = snap.roads && snap.roads[pside]; if (!rd || !rd.pulses) return null;
                const pu = rd.pulses.find((p: any) => p.key === selPulse); if (!pu || !pu.shown) return null;
                const c = toPx({ x: pu.cx, y: pu.cy });
                return (
                  <svg width={dW} height={dH} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
                    <circle cx={c.x} cy={c.y} r={22} fill="none" stroke="#e67e22" strokeWidth={2} strokeDasharray="4,3"
                      style={{ cursor: 'nwse-resize', pointerEvents: 'all' }} onPointerDown={(e) => startPulseDrag(e, selPulse, true)} />
                    {/* rotate grip */}
                    <line x1={c.x} y1={c.y} x2={c.x} y2={c.y - 34} stroke="#16a085" strokeWidth={1.5} />
                    <circle cx={c.x} cy={c.y - 34} r={5} fill="transparent" stroke="#16a085" strokeWidth={2.5}
                      style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startPulseRotate(e, selPulse)} />
                    <circle cx={c.x} cy={c.y} r={5} fill="transparent" stroke="#9b59b6" strokeWidth={2.5}
                      style={{ cursor: 'grab', pointerEvents: 'all' }} onPointerDown={(e) => startPulseDrag(e, selPulse, false)}
                      onDoubleClick={() => setPulseFlag(selPulse, false)} />
                  </svg>
                );
              })()}
              {/* Inline rich-text editor: formatting toolbar + multi-line textarea
                  (Enter = line break) + a Save button. */}
              {editing && (() => {
                const taRef = editRef;
                const tbtn = (active: boolean): React.CSSProperties => ({
                  minWidth: 26, height: 26, padding: '0 6px', borderRadius: 5, cursor: 'pointer', fontSize: 12, lineHeight: 1,
                  background: active ? '#FFD93D' : '#1f1f1f', color: active ? '#1a1a1a' : '#ddd', border: '1px solid ' + (active ? '#FFD93D' : '#3a3a3a'),
                });
                const PANEL_H = 220, PANEL_W = 388;
                const below = editing.top + editing.eh + 10;
                const autoTop = (below + PANEL_H <= dH) ? below : Math.max(2, editing.top - PANEL_H - 10);
                const autoLeft = Math.max(2, Math.min(editing.left - 10, dW - PANEL_W));
                const panelLeft = editPos ? editPos.left : autoLeft;
                const panelTop = editPos ? editPos.top : autoTop;
                const startPanelDrag = (ev: React.PointerEvent) => {
                  ev.preventDefault(); ev.stopPropagation();
                  const sx = ev.clientX, sy = ev.clientY, bx = panelLeft, by = panelTop;
                  const mv = (e: PointerEvent) => setEditPos({ left: bx + (e.clientX - sx), top: by + (e.clientY - sy) });
                  const u2 = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', u2); };
                  window.addEventListener('pointermove', mv); window.addEventListener('pointerup', u2);
                };
                return (
                  <div style={{ position: 'absolute', left: panelLeft, top: panelTop, width: PANEL_W, boxSizing: 'border-box', zIndex: 6, background: '#0d0d0d', border: '1px solid #2a2a2a', borderRadius: 10, padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,.55)' }}
                    onPointerDown={(e) => e.stopPropagation()}>
                    <div onPointerDown={startPanelDrag} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'move', marginBottom: 8, color: '#888', fontSize: 11, fontWeight: 600, userSelect: 'none' }}>
                      <span style={{ letterSpacing: 1 }}>⠿</span> Edit text — drag to move
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <button style={{ ...tbtn(editing.b), fontWeight: 800 }} title="Bold" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ b: !editing.b }); }}>B</button>
                      <button style={{ ...tbtn(editing.i), fontStyle: 'italic' }} title="Italic" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ i: !editing.i }); }}>I</button>
                      <button style={{ ...tbtn(editing.u), textDecoration: 'underline' }} title="Underline" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ u: !editing.u }); }}>U</button>
                      <span style={{ width: 1, height: 18, background: '#333', margin: '0 2px' }} />
                      <button style={tbtn(editing.align === 'start')} title="Align left" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ align: 'start' }); }}>L</button>
                      <button style={tbtn(editing.align === 'middle' || !editing.align)} title="Align center" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ align: 'middle' }); }}>C</button>
                      <button style={tbtn(editing.align === 'end')} title="Align right" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ align: 'end' }); }}>R</button>
                      <span style={{ width: 1, height: 18, background: '#333', margin: '0 2px' }} />
                      <button style={tbtn(editing.case === 'upper')} title="ALL CAPS" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ case: editing.case === 'upper' ? '' : 'upper' }); }}>AA</button>
                      <button style={tbtn(editing.case === 'lower')} title="lowercase" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ case: editing.case === 'lower' ? '' : 'lower' }); }}>aa</button>
                      <button style={tbtn(editing.case === 'title')} title="Title Case" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ case: editing.case === 'title' ? '' : 'title' }); }}>Aa</button>
                      <button style={tbtn(editing.sc)} title="Small caps" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ sc: !editing.sc }); }}>S&#x1D04;</button>
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, alignItems: 'center' }}>
                      <span style={{ color: '#888', fontSize: 10, marginRight: 2 }}>Size</span>
                      <select value={editing.fs || ''} onChange={(e) => setFieldStyle({ fs: e.target.value ? parseInt(e.target.value, 10) : 0 })}
                        style={{ background: '#1f1f1f', color: '#ddd', border: '1px solid #3a3a3a', borderRadius: 5, padding: '4px 6px', fontSize: 12, outline: 'none', cursor: 'pointer' }}>
                        <option value="">Auto</option>
                        {Array.from({ length: 13 }, (_, n) => 8 + n).map((px) => <option key={px} value={px}>{px}px</option>)}
                      </select>
                      <span style={{ width: 1, height: 18, background: '#333', margin: '0 2px' }} />
                      <span style={{ color: '#888', fontSize: 10, marginRight: 2 }}>Line</span>
                      <button style={tbtn(false)} title="Tighter lines" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ lh: Math.max(0.8, Math.round(((editing.lh || 1.2) - 0.1) * 10) / 10) }); }}>&minus;</button>
                      <button style={tbtn(false)} title="Looser lines" onMouseDown={(e) => { e.preventDefault(); setFieldStyle({ lh: Math.min(2.5, Math.round(((editing.lh || 1.2) + 0.1) * 10) / 10) }); }}>+</button>
                      <span style={{ color: '#777', fontSize: 10, minWidth: 26, textAlign: 'center' }}>{(editing.lh || 1.2).toFixed(1)}</span>
                    </div>
                    <textarea ref={taRef} autoFocus defaultValue={editing.value} rows={Math.min(6, Math.max(2, (editing.value.match(/\n/g) || []).length + 2))}
                      onChange={(e) => liveEditApply(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }}
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: '#161616', color: '#fff', border: '1px solid #2a2a2a', borderRadius: 6, padding: '8px 9px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.3, outline: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                      <span style={{ color: '#777', fontSize: 10 }}>Enter = new line</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setEditing(null)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #3a3a3a', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                        <button onClick={() => commitEdit(taRef.current ? taRef.current.value : editing.value)} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#FFD93D', color: '#1a1a1a', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Save</button>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {note && (
                <div style={{ position: 'absolute', left: '50%', top: 10, transform: 'translateX(-50%)', zIndex: 7, background: 'rgba(13,13,13,.96)', color: '#FFD93D', border: '1px solid #2f7bff', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,.5)', pointerEvents: 'none' }}>{note}</div>
              )}
            </div>
          </div>
          {/* Cleanup prompt floats over the canvas (so it stays visible when zoomed). */}
          {snap && showCleanup && (
            <div style={{ position: 'absolute', left: '50%', top: 12, transform: 'translateX(-50%)', zIndex: 9, display: 'flex', alignItems: 'center', gap: 10, maxWidth: '92%', background: 'rgba(13,13,13,.97)', border: '1px solid #FFD93D', borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,.55)' }}>
              <span style={{ color: '#ddd', fontSize: 12 }}>Plot name &amp; inside dimensions overlap the house.</span>
              <button onClick={cleanupPlot} style={{ background: '#FFD93D', color: '#1a1a1a', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Hide name &amp; dims out</button>
              <button onClick={() => setShowCleanup(false)} title="Dismiss" style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', display: 'flex' }}><X size={14} /></button>
            </div>
          )}
          {/* Zoom controls (floating over the canvas) */}
          {snap && (
            <div style={{ position: 'absolute', left: '50%', bottom: 12, transform: 'translateX(-50%)', zIndex: 8, display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(13,13,13,.94)', border: '1px solid #2a2a2a', borderRadius: 10, padding: '6px 10px', boxShadow: '0 6px 20px rgba(0,0,0,.5)' }}>
              <button onClick={() => applyZoom(zoom * 0.8)} title="Zoom out" style={zoomBtn}>&minus;</button>
              <input type="range" min={50} max={400} step={5} value={Math.min(400, Math.max(50, Math.round(zoom * 100)))} onChange={(e) => applyZoom(parseInt(e.target.value, 10) / 100)} style={{ width: 130, accentColor: '#FFD93D', cursor: 'pointer' }} />
              <button onClick={() => applyZoom(zoom * 1.25)} title="Zoom in" style={zoomBtn}>+</button>
              <span style={{ color: '#aaa', fontSize: 11, minWidth: 36, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
              <button onClick={fitScreen} title="Fit page to screen" style={{ ...zoomBtn, width: 'auto', padding: '0 8px', fontSize: 11 }}>Fit</button>
            </div>
          )}
          </div>

          {/* RIGHT on desktop / BOTTOM sheet on mobile: settings */}
          <div style={{ ...(isMobile
            ? { width: 'auto', maxHeight: '45vh', borderTop: '1px solid #222' }
            : { width: 300, borderLeft: '1px solid #222' }),
            flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: 18, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
              {/* Tabs: Elements / Free draw / Presets */}
              <div style={{ display: 'flex', gap: 3, background: '#1a1a1a', borderRadius: 8, padding: 3, border: '1px solid #2a2a2a' }}>
                {([['elements', 'Elements'], ['draw', 'Free draw'], ['preset', 'Presets']] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setPanelTab(k)}
                    style={{ flex: 1, background: panelTab === k ? '#FFD93D' : 'transparent', color: panelTab === k ? '#1a1a1a' : '#bbb', border: 'none', borderRadius: 6, padding: '6px 4px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                ))}
              </div>

              {panelTab === 'elements' && (
                <div>
                  <div style={{ color: '#aaa', fontSize: 11, marginBottom: 6 }}>Tap to add an element</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {H_TYPES.map((t) => {
                      // Each element shows its shape glyph; stairs stays distinct (special element).
                      const isStairs = t.key === 'stairs';
                      const icon = t.key === 'circle'
                        ? <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}><circle cx="8" cy="8" r="6" /></svg>
                        : t.key === 'trapezium'
                          ? <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round"><path d="M4 4h8l2 8H2z" /></svg>
                          : isStairs
                            ? <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M2 13h4v-3h4V7h4V4" /></svg>
                            : <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}><rect x="2.5" y="4" width="11" height="8" rx="1" /></svg>;
                      return (
                        <button key={t.key} onClick={() => addRoom(t.key)} title={`Add ${t.label}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#1a1a1a',
                            color: isStairs ? '#8fb7cc' : '#ddd',
                            border: isStairs ? '1px dashed #3f6070' : '1px solid #333',
                            borderRadius: 7, padding: '6px 9px', fontSize: 12, cursor: 'pointer' }}>
                          {icon} {t.label}
                        </button>
                      );
                    })}
                    <button onClick={addDim} title="Add a dimension arrow (measures its own length; drag ends to extend/aim)"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#1a1a1a', color: '#e57373', border: '1px dashed #7a3b3b', borderRadius: 7, padding: '6px 9px', fontSize: 12, cursor: 'pointer' }}>
                      <Ruler size={13} /> Dimension
                    </button>
                  </div>
                  {/* Scale the whole house (all rooms + dimensions) at once. */}
                  <button onClick={() => { setScaleMode((v) => !v); setSelRoom(null); setToolbarRoom(null); }}
                    title="Scale the whole house — drag the green corner handles"
                    style={{ marginTop: 12, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      background: scaleMode ? '#2ecc71' : '#1a1a1a', color: scaleMode ? '#0a0a0a' : '#ddd',
                      border: '1px solid ' + (scaleMode ? '#2ecc71' : '#333'), borderRadius: 7, padding: '8px 9px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    <Maximize2 size={13} /> {scaleMode ? 'Scaling — drag corners · tap to finish' : 'Scale whole house'}
                  </button>
                </div>
              )}

              {panelTab === 'draw' && (
                <div>
                  {/* Tool: drag a shape, or connect dots into a polygon. */}
                  <div style={{ display: 'flex', gap: 3, background: '#1a1a1a', borderRadius: 8, padding: 3, border: '1px solid #2a2a2a', marginBottom: 8 }}>
                    {([['shape', 'Drag shape'], ['line', 'Line / connect dots']] as const).map(([k, l]) => (
                      <button key={k} onClick={() => { setDrawTool(k); setDrawRect(null); setPolyPts([]); }}
                        style={{ flex: 1, background: drawTool === k ? '#FFD93D' : 'transparent', color: drawTool === k ? '#1a1a1a' : '#bbb', border: 'none', borderRadius: 6, padding: '6px 4px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>{l}</button>
                    ))}
                  </div>
                  <button onClick={() => { setDrawMode((v) => !v); setPolyPts([]); }} style={{ ...seg(drawMode), width: '100%', justifyContent: 'center' }}>
                    <PenLine size={14} /> {drawMode ? 'Drawing — click to stop' : (drawTool === 'line' ? 'Draw a figure' : 'Draw a room')}
                  </button>
                  {drawTool === 'shape' ? (
                    <>
                      <div style={{ color: '#666', fontSize: 10, marginTop: 8, marginBottom: 6 }}>New shape:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {H_TYPES.map((t) => (
                          <button key={t.key} onClick={() => setCurType(t.key)}
                            style={{ ...seg(curType === t.key), padding: '4px 8px', fontSize: 11 }}>{t.label}</button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ color: '#888', fontSize: 11, marginTop: 8, lineHeight: 1.4 }}>
                        Click to drop points, then click the first dot (or double-click / Finish) to close the figure.
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button onClick={finishPoly} disabled={polyPts.length < 3}
                          style={{ flex: 1, background: polyPts.length >= 3 ? '#FFD93D' : '#2a2a2a', color: polyPts.length >= 3 ? '#1a1a1a' : '#666', border: 'none', borderRadius: 6, padding: '7px 8px', fontSize: 12, fontWeight: 700, cursor: polyPts.length >= 3 ? 'pointer' : 'not-allowed' }}>
                          Finish ({polyPts.length})
                        </button>
                        <button onClick={() => setPolyPts((c) => c.slice(0, -1))} disabled={!polyPts.length} title="Undo last point"
                          style={{ background: 'transparent', color: '#ddd', border: '1px solid #3a3a3a', borderRadius: 6, padding: '7px 10px', fontSize: 12, cursor: polyPts.length ? 'pointer' : 'not-allowed' }}>Undo pt</button>
                        <button onClick={() => setPolyPts([])} disabled={!polyPts.length} title="Clear points"
                          style={{ background: 'transparent', color: '#e57373', border: '1px solid #5a2a2a', borderRadius: 6, padding: '7px 10px', fontSize: 12, cursor: polyPts.length ? 'pointer' : 'not-allowed' }}>Clear</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {panelTab === 'preset' && (
                <div>
                  <div style={{ color: '#aaa', fontSize: 11, marginBottom: 8 }}>Tap a preset to place it on the plot</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {presetCard(
                      <svg viewBox="0 0 120 74" width="100%" style={{ display: 'block' }}>
                        <rect x="30" y="12" width="66" height="44" fill="#fff" stroke="#141414" strokeWidth={1.5} />
                        <rect x="34" y="16" width="58" height="36" fill="none" stroke="#141414" strokeWidth={1} />
                        <rect x="56" y="52" width="14" height="6" fill="#fff" />
                        <path d="M70 56 A14 14 0 0 1 56 42" fill="none" stroke="#888" strokeWidth={0.8} strokeDasharray="2 1.5" />
                        {thumbDims}
                      </svg>,
                      'Single room', 'Door · double wall', true, () => requestPreset(() => buildPreset('single')))}
                    {presetCard(
                      <svg viewBox="0 0 120 74" width="100%" style={{ display: 'block' }}>
                        <rect x="30" y="12" width="66" height="44" fill="#fff" stroke="#141414" strokeWidth={1.5} />
                        <line x1="63" y1="12" x2="63" y2="56" stroke="#141414" strokeWidth={1.4} />
                        <rect x="34" y="16" width="25" height="36" fill="none" stroke="#141414" strokeWidth={0.9} />
                        <rect x="67" y="16" width="25" height="36" fill="none" stroke="#141414" strokeWidth={0.9} />
                        <rect x="43" y="52" width="10" height="6" fill="#fff" /><rect x="73" y="52" width="10" height="6" fill="#fff" />
                        {thumbDims}
                      </svg>,
                      'Double room', 'Two rooms · doors', true, () => requestPreset(() => buildPreset('double')))}
                    {presetCard(
                      <svg viewBox="0 0 120 74" width="100%" style={{ display: 'block' }}>
                        <rect x="30" y="12" width="66" height="44" fill="#fff" stroke="#141414" strokeWidth={1.5} />
                        <line x1="63" y1="12" x2="63" y2="34" stroke="#141414" strokeWidth={1.3} /><line x1="30" y1="34" x2="96" y2="34" stroke="#141414" strokeWidth={1.3} />
                        <rect x="44" y="30" width="8" height="5" fill="#fff" /><rect x="74" y="30" width="8" height="5" fill="#fff" /><rect x="58" y="52" width="10" height="6" fill="#fff" />
                        {thumbDims}
                      </svg>,
                      'Two bed + hall', 'Bedrooms + hall', true, () => requestPreset(() => buildPreset('twoBedHall')))}
                    {presetCard(
                      <svg viewBox="0 0 120 74" width="100%" style={{ display: 'block' }}>
                        <rect x="30" y="12" width="66" height="44" fill="#fff" stroke="#141414" strokeWidth={1.5} />
                        <line x1="70" y1="12" x2="70" y2="56" stroke="#141414" strokeWidth={1.3} /><line x1="70" y1="34" x2="96" y2="34" stroke="#141414" strokeWidth={1.3} />
                        <g stroke="#8a93a0" strokeWidth={0.7}><line x1="74" y1="14" x2="74" y2="32" /><line x1="79" y1="14" x2="79" y2="32" /><line x1="84" y1="14" x2="84" y2="32" /><line x1="89" y1="14" x2="89" y2="32" /></g>
                        <rect x="46" y="52" width="10" height="6" fill="#fff" /><rect x="78" y="52" width="10" height="6" fill="#fff" />
                        {thumbDims}
                      </svg>,
                      'House + stairs', 'Rooms · stairs · doors', true, () => requestPreset(() => buildPreset('houseStairs')))}
                  </div>
                </div>
              )}

              <div style={{ borderTop: '1px solid #222', paddingTop: 12 }}>
                <div style={{ color: '#aaa', fontSize: 11, marginBottom: 6 }}>Walls &amp; labels</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <button onClick={() => setWall2('single')} style={{ ...seg(wall === 'single'), flex: 1, justifyContent: 'center' }}>Single</button>
                  <button onClick={() => setWall2('double')} style={{ ...seg(wall === 'double'), flex: 1, justifyContent: 'center' }}>Double</button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#bbb', fontSize: 12 }}>Show labels</span>
                  <button onClick={() => setLabels2(!labels)} style={{ width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: labels ? '#FFD93D' : '#3a3a3a', position: 'relative', padding: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: labels ? 20 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff' }} />
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ color: '#bbb', fontSize: 12 }}>Merge overlaps<span style={{ color: '#666', fontSize: 10, display: 'block' }}>open the wall where rooms overlap</span></span>
                  <button onClick={() => setAmalg2(!amalgamate)} title="Merge overlapping rooms into one shape" style={{ width: 38, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: amalgamate ? '#FFD93D' : '#3a3a3a', position: 'relative', padding: 0, flexShrink: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: amalgamate ? 20 : 2, width: 16, height: 16, borderRadius: 8, background: '#fff' }} />
                  </button>
                </div>
              </div>

              {selRoom && (
                <button onClick={() => deleteRoom(selRoom)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'transparent', color: '#e57373', border: '1px solid #5a2a2a', borderRadius: 7, padding: '8px', fontSize: 12, cursor: 'pointer' }}>
                  <Trash2 size={14} /> Delete selected room
                </button>
              )}

              <div style={{ color: '#666', fontSize: 11, lineHeight: 1.5 }}>
                The plot is faded for reference. Add elements or a template, drag rooms to place them, click a room then Delete to remove it.
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '12px 18px', borderTop: '1px solid #222', flexShrink: 0 }}>
              <button onClick={cancel} style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: '1px solid #333', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={onClose} style={{ flex: 1, padding: '9px 14px', borderRadius: 8, border: 'none', background: '#FFD93D', color: '#1a1a1a', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Apply</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default House2Editor;
